/*
  Tenchi theme script. No dependencies.
  Reads #tc-data (snippets/tc-data.liquid), keeps every cart-bound control in sync with the Shopify cart
  through the cart AJAX API, and runs the tray (cart drawer), the PROPOSED quote engine and the page modules.
  Stock is never shown as a number: only "Low stock", and limit messages carry no counts.
*/
(() => {
  'use strict';

  document.querySelectorAll('script[type="application/ld+json"]').forEach((el) => {
    try {
      const data = JSON.parse(el.textContent);
      const walk = (n) => {
        if (!n || typeof n !== 'object') return;
        if (Array.isArray(n)) {
          n.forEach(walk);
          return;
        }
        if (n.sku != null && /MNU|TC-/i.test(String(n.sku))) delete n.sku;
        Object.keys(n).forEach((k) => walk(n[k]));
      };
      walk(data);
      el.textContent = JSON.stringify(data);
    } catch (e) {}
  });

  const dataEl = document.getElementById('tc-data');
  if (!dataEl) return;
  let D;
  try {
    D = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }

  /* ---------- Utilities ---------- */

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const on = (type, sel, fn, capture) =>
    document.addEventListener(
      type,
      (e) => {
        const t = e.target instanceof Element ? e.target : e.target && e.target.parentElement;
        const el = t && t.closest(sel);
        if (el) fn(e, el);
      },
      capture || false
    );
  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const ta = document.createElement('textarea');
  const decode = (s) => {
    ta.innerHTML = String(s == null ? '' : s);
    return ta.value;
  };
  const store = {
    get(k, d) {
      try {
        const v = localStorage.getItem(k);
        return v == null ? d : JSON.parse(v);
      } catch (e) {
        return d;
      }
    },
    set(k, v) {
      try {
        localStorage.setItem(k, JSON.stringify(v));
      } catch (e) {}
    }
  };
  const R = D.routes || {};
  const offers = Object.assign(
    { volume_ladder: 'live', sample_offer: 'live', clearance: 'hidden', sharps_deal: 'hidden', packs_bundles: 'hidden' },
    D.offers || {}
  );
  const shown = (k) => (offers[k] || 'hidden') !== 'hidden';
  const proposed = (k) => offers[k] === 'proposed';
  const VOL = shown('volume_ladder');
  const CLR = shown('clearance');
  const SHARPS_OFFER = shown('sharps_deal');
  const BUNDLES = shown('packs_bundles');

  const STR = {};
  Object.keys(D.t || {}).forEach((k) => {
    STR[k] = decode(D.t[k]);
  });
  const T = (k, v) =>
    (STR[k] != null ? STR[k] : k).replace(/\[(\w+)\]/g, (m, n) => (v && v[n] != null ? String(v[n]) : m));
  const P = (k, n, v) => T(k + (n === 1 ? '_one' : '_other'), Object.assign({ count: n }, v));
  let tap = null;
  let lastItems = null;

  const FMT = decode(D.moneyFormat || '${{amount}}');
  const FMT_M = FMT.match(/\{\{\s*(\w+)\s*\}\}/);
  const FMT_KIND = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_no_decimals_with_space_separator: [0, ' ', ','],
    amount_with_space_separator: [2, ' ', ','],
    amount_with_period_and_space_separator: [2, ' ', '.']
  };
  const money = (cents) => {
    const n = Math.abs(Math.round(Number(cents) || 0));
    const k = FMT_KIND[FMT_M ? FMT_M[1] : 'amount'] || FMT_KIND.amount;
    const parts = (n / 100).toFixed(k[0]).split('.');
    const num = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, k[1]) + (parts[1] ? k[2] + parts[1] : '');
    return FMT_M ? FMT.replace(FMT_M[0], num) : num;
  };
  const moneyShort = (cents) => (Math.round(cents) % 100 === 0 ? money(cents).replace(/[.,]00(?!\d)/, '') : money(cents));
  const minus = (cents) => '\u2212' + money(cents);
  const mh = (cents) => esc(money(cents));
  const mnh = (cents) => esc(minus(cents));
  const tag = (k) => (proposed(k) ? ` <span class="prop mono" title="${esc(T('proposed_title'))}">${esc(T('proposed_tag'))}</span>` : '');
  const debounce = (fn, ms) => {
    let t;
    return (...a) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...a), ms);
    };
  };

  /* ---------- Catalog (Theme settings > Needle catalog) ---------- */

  const BY = new Map();
  const NEEDLE_PRODUCTS = new Set();
  (D.needles || []).forEach((n) => {
    NEEDLE_PRODUCTS.add(n.product);
    n.variants.forEach((v) => {
      BY.set(v.id, {
        id: v.id,
        kind: 'needle',
        box: n.box,
        d: n.d,
        len: v.len,
        key: v.key,
        color: n.color,
        price: v.price,
        stock: v.stock,
        max: v.max,
        url: v.url,
        label: T('needle_label', { box: n.box, d: n.d, len: v.len }),
        short: T('size', { d: n.d, len: v.len })
      });
    });
  });
  (D.acc || []).forEach((a) => {
    BY.set(a.id, { id: a.id, kind: 'acc', role: a.key, price: a.price, stock: a.stock, max: a.max, url: a.url, label: a.name, short: a.name, color: '' });
  });
  const SHARPS = (D.acc || []).find((a) => a.key === 'sharps');
  const LIST_BOX = Math.min(...Array.from(BY.values()).filter((s) => s.kind === 'needle').map((s) => s.price).concat([799]));
  const CASES = (D.cases || []).filter((c) => c.price > 0).sort((a, b) => a.size - b.size);
  const FREE = Number(D.freeShip) || 0;
  const HKEY = 'tenchi-history-v1';

  /* ---------- Quote engine (per-offer advertised prices) ---------- */

  const floor99 = (x) => Math.floor((x + 1 + 1e-6) / 100) * 100 - 1;
  const near99 = (x) => {
    const d = floor99(x);
    const u = d + 100;
    return x - d <= u - x ? d : u;
  };
  const ceil99 = (x) => {
    x = Math.round(x);
    if (x <= 0) return 0;
    const rem = x % 100;
    return rem === 99 ? x : x + ((99 - rem + 100) % 100);
  };

  /* Mix-any-size volume ladder. Shopify discounts are fixed per-box amounts; display % is vs $7.99. 250+ and 500+ mirror the Shopify automatic discounts. */
  const VOLUME = [
    { min: 500, pct: 37.5, box: 499 },
    { min: 250, pct: 31.3, box: 549 },
    { min: 100, pct: 25, box: 599 },
    { min: 50, pct: 20, box: 639 },
    { min: 25, pct: 15, box: 679 },
    { min: 10, pct: 12.5, box: 699 },
    { min: 5, pct: 10, box: 719 }
  ];
  const LADDER = VOLUME.map((t) => t.min).sort((a, b) => a - b);

  function volumePct(n) {
    n = Math.max(0, n | 0);
    for (const t of VOLUME) if (n >= t.min) return t.pct;
    return 0;
  }
  function perBox(n, unit) {
    n = Math.max(0, n | 0);
    for (const t of VOLUME) if (n >= t.min) return t.box;
    return Number(unit) || LIST_BOX;
  }
  function volumeCost(n, unit) {
    n = Math.max(0, n | 0);
    return Math.round(n * perBox(n, unit));
  }
  function advertisedAt(n, unit) {
    n = Math.max(0, n | 0);
    unit = Number(unit) || LIST_BOX;
    const exact = CASES.find((c) => c.size === n);
    if (exact && exact.price > 0) return exact.price;
    return volumeCost(n, unit);
  }

  /** Advertised cost for n volume-eligible needle boxes (clearance sizes stay off this ladder). */
  function casePlan(n, unit) {
    n = Math.max(0, n | 0);
    unit = Number(unit) || LIST_BOX;
    const pct = volumePct(n);
    return {
      cost: advertisedAt(n, unit),
      shop: volumeCost(n, unit),
      pct,
      n,
      cases: pct ? [{ size: n, count: 1, pct }] : [],
      loose: pct ? 0 : n
    };
  }

  /** Next ladder stop above n eligible boxes. */
  function nextCase(n) {
    n = Math.max(0, n | 0);
    const m = LADDER.find((s) => s > n);
    if (!m) return null;
    return { boxes: m, more: m - n, price: advertisedAt(m, LIST_BOX), order: false, pct: volumePct(m) };
  }

  const clearanceFor = (id) => {
    const s = BY.get(id);
    return s && s.key && D.clearance && D.clearance[s.key] != null ? D.clearance[s.key] : null;
  };

  /**
   * m.loose:   [{id, qty, price, needle}] priced by the case plan, clearance and the sharps offer
   * m.bundles: [{name, price, lines}] intact packs and class kits at their fixed price
   * m.fixed:   [{id, qty, price, label}] lines priced as-is (subscriptions, sample, other products)
   */
  function quote(m) {
    const save = { cases: 0, clearance: 0, sharps: 0, bundles: 0 };
    const bundles = [];
    const all = [];
    let list = 0;
    let prop = 0;
    let boxes = 0;
    (m.bundles || []).forEach((b) => {
      const bl = b.lines.reduce((n, l) => n + l.price * l.qty, 0);
      const key = b.offerKey || 'packs_bundles';
      list += bl;
      b.lines.forEach((l) => {
        if (l.needle) boxes += l.qty;
        all.push(l);
      });
      if (shown(key) && b.price > 0 && b.price < bl) {
        prop += b.price;
        save.bundles += bl - b.price;
        bundles.push({ name: b.name, save: bl - b.price, price: b.price, offerKey: key });
      } else prop += bl;
    });
    let n = 0;
    let clr = 0;
    let clrCost = 0;
    let clrList = 0;
    let needleList = 0;
    (m.loose || []).forEach((l) => {
      if (l.qty <= 0) return;
      all.push(l);
      list += l.price * l.qty;
      if (!l.needle) return;
      boxes += l.qty;
      n += l.qty;
      needleList += l.price * l.qty;
      const c = CLR ? clearanceFor(l.id) : null;
      if (c != null && c < l.price) {
        clr += l.qty;
        clrCost += c * l.qty;
        clrList += l.price * l.qty;
      }
    });
    const rest = n - clr;
    let plan = { cases: [], loose: rest, pct: 0, n: rest };
    if (!VOL) prop += needleList - clrList + (CLR ? clrCost : clrList);
    else {
      const b = casePlan(rest, rest ? (needleList - clrList) / rest : LIST_BOX);
      prop += b.cost + (CLR ? clrCost : clrList);
      save.cases += needleList - clrList - b.cost;
      plan = b;
    }
    if (CLR) save.clearance += clrList - clrCost;
    (m.loose || []).forEach((l) => {
      if (l.needle || l.qty <= 0) return;
      let unit = l.price;
      if (SHARPS_OFFER && SHARPS && l.id === SHARPS.id && boxes > 0 && D.sharpsWith > 0 && D.sharpsWith < l.price) unit = D.sharpsWith;
      prop += unit * l.qty;
      save.sharps += (l.price - unit) * l.qty;
    });
    (m.fixed || []).forEach((l) => {
      all.push(l);
      list += l.price * l.qty;
      prop += l.price * l.qty;
      if (l.needle) boxes += l.qty;
    });
    Object.keys(save).forEach((k) => {
      save[k] = Math.round(save[k]);
    });
    list = Math.round(list);
    prop = Math.round(prop);
    return {
      boxes,
      volumeBoxes: rest,
      list,
      proposed: prop,
      savings: list - prop,
      save,
      cases: plan.cases,
      loose: plan.loose,
      pct: plan.pct || 0,
      bundles,
      next: nextCase(rest),
      lines: all
    };
  }

  const caseText = (cs) => {
    if (!cs || !cs.length) return '';
    return cs
      .map((c) => {
        if (c.pct && !CASES.some((x) => x.size === c.size)) return T('volume_price', { pct: String(c.pct), n: c.size });
        return T('case_count', { count: c.count, size: c.size });
      })
      .join(' + ');
  };
  const savePct = (save, list) => {
    if (!(list > 0) || !(save > 0)) return '0';
    const p = Math.round((save / list) * 1000) / 10;
    return String(p);
  };

  /* ---------- Cart store ---------- */

  let cart = D.cart || { items: [], item_count: 0, total_price: 0, attributes: {}, note: '' };
  const props = (it) => it.properties || {};
  const SAMPLE = D.sample || {};
  const isSample = (it) => {
    if (props(it)._offer === 'free-sample') return true;
    if (SAMPLE.id && (it.product_id === SAMPLE.id || it.id === SAMPLE.id)) return true;
    const tags = it.tags || (it.product && it.product.tags) || '';
    const tagStr = Array.isArray(tags) ? tags.join(',') : String(tags);
    if (/(^|,)\s*sample-pack\s*(,|$)/i.test(tagStr)) return true;
    const h = it.handle || '';
    if (h === 'sample-pack' || h === 'free-sample-needles' || (SAMPLE.handle && h === SAMPLE.handle)) return true;
    return false;
  };
  const isLoose = (it) => !props(it)._bundle && !props(it)._bundle_id && !props(it)._offer && !it.selling_plan_allocation && !isSample(it);
  const isNeedleLine = (it) => NEEDLE_PRODUCTS.has(it.product_id) && !isSample(it);
  const checkoutHref = () => (R.cart || '/cart').replace(/\/cart$/, '') + '/checkout';
  const want = new Map();
  const busy = new Set();
  const looseQty = (vid) => cart.items.reduce((n, it) => n + (it.variant_id === vid && isLoose(it) ? it.quantity : 0), 0);
  const allQty = (vid) => cart.items.reduce((n, it) => n + (it.variant_id === vid ? it.quantity : 0), 0);
  const qtyOf = (vid) => (want.has(vid) ? want.get(vid) : looseQty(vid));
  const totalQty = (vid) => allQty(vid) - looseQty(vid) + qtyOf(vid);
  const labelOf = (vid, it) => {
    const s = BY.get(vid);
    if (s) return s.label;
    if (it) return it.variant_title && it.variant_title !== 'Default Title' ? `${it.product_title} \u00b7 ${it.variant_title}` : it.product_title;
    return '';
  };
  /** How many more of a variant may go in the cart. Only low-stock variants carry a cap. */
  const capAdd = (vid) => {
    const s = BY.get(vid);
    if (!s) return Infinity;
    if (s.stock === 'out') return 0;
    return s.max != null ? Math.max(0, s.max - totalQty(vid)) : Infinity;
  };
  const pendingDelta = () => {
    let boxes = 0;
    let cents = 0;
    let items = 0;
    want.forEach((q, vid) => {
      const d = q - looseQty(vid);
      const s = BY.get(vid);
      const it = cart.items.find((x) => x.variant_id === vid);
      items += d;
      cents += d * (s ? s.price : it ? it.final_price : 0);
      if (s ? s.kind === 'needle' : it && isNeedleLine(it)) boxes += d;
    });
    kwant.forEach((q, key) => {
      const it = cart.items.find((x) => x.key === key);
      if (!it) return;
      const d = q - it.quantity;
      items += d;
      cents += d * it.final_price;
      if (isNeedleLine(it)) boxes += d;
    });
    return { boxes, cents, items };
  };
  const boxesNow = () => cart.items.reduce((n, it) => n + (isNeedleLine(it) ? it.quantity : 0), 0) + pendingDelta().boxes;
  const itemsNow = () => (cart.item_count || 0) + pendingDelta().items;
  const totalNow = () => (cart.total_price || 0) + pendingDelta().cents;
  const cartPo = () => ((cart.attributes || {})['PO number'] || '').toString();
  const RX_KEY = 'Practitioner confirmation';
  const RX_AT = 'Practitioner confirmation at';
  const rxConfirmed = () => String((cart.attributes || {})[RX_KEY] || '') === 'Yes';

  /** Splits the cart into loose lines, packs/kits (intact or changed), the sample and fixed lines. */
  function groups() {
    const loose = [];
    const sample = [];
    const fixed = [];
    const map = new Map();
    cart.items.forEach((it) => {
      const p = props(it);
      if (it.selling_plan_allocation) fixed.push(it);
      else if (p._bundle_id || p._bundle) {
        const id = p._bundle_id || p._bundle;
        if (!map.has(id)) map.set(id, { id, name: p._bundle || '', price: parseInt(p._bundle_price, 10) || 0, n: parseInt(p._bundle_n, 10) || 0, offerKey: p._offer_key || '', items: [] });
        map.get(id).items.push(it);
      } else if (isSample(it)) sample.push(it);
      else if (p._offer) fixed.push(it);
      else loose.push(it);
    });
    const bundles = Array.from(map.values());
    bundles.forEach((b) => {
      const tot = b.items.reduce((n, it) => n + it.quantity, 0);
      b.intact = b.n > 0 && tot === b.n && b.items.every((it) => !props(it)._bundle_q || parseInt(props(it)._bundle_q, 10) === it.quantity);
      if (!b.offerKey) {
        const hasAcc = b.items.some((it) => {
          const s = BY.get(it.variant_id);
          return s && s.kind === 'acc';
        });
        b.offerKey = hasAcc ? 'packs_bundles' : 'volume_ladder';
      }
    });
    return { loose, bundles, sample, fixed };
  }

  const lineOf = (id, qty, price, it) => {
    const s = BY.get(id);
    return { id, qty, price, needle: s ? s.kind === 'needle' : !!(it && isNeedleLine(it)), label: labelOf(id, it) };
  };

  function cartModel() {
    const g = groups();
    const loose = new Map();
    const put = (id, qty, price, it) => {
      const l = loose.get(id) || lineOf(id, 0, price, it);
      l.qty += qty;
      loose.set(id, l);
    };
    g.loose.forEach((it) => put(it.variant_id, it.quantity, it.original_price, it));
    want.forEach((q, vid) => {
      const s = BY.get(vid);
      const l = loose.get(vid) || lineOf(vid, 0, s ? s.price : 0);
      l.qty = q;
      loose.set(vid, l);
    });
    const bundles = [];
    g.bundles.forEach((b) => {
      if (b.intact && b.price > 0) bundles.push({ name: b.name, price: b.price, offerKey: b.offerKey, lines: b.items.map((it) => lineOf(it.variant_id, it.quantity, it.original_price, it)) });
      else b.items.forEach((it) => put(it.variant_id, it.quantity, it.original_price, it));
    });
    const fixed = g.fixed.map((it) => Object.assign(lineOf(it.variant_id, it.quantity, it.final_price, it), { label: it.selling_plan_allocation ? `${labelOf(it.variant_id, it)} \u00b7 ${it.selling_plan_allocation.selling_plan.name}` : labelOf(it.variant_id, it) }));
    g.sample.forEach((it) => {
      fixed.push(Object.assign(lineOf(it.variant_id, it.quantity, it.original_price, it), { label: T('sample_line'), needle: false }));
    });
    return { loose: Array.from(loose.values()).filter((l) => l.qty > 0), bundles, fixed };
  }
  const cartQuote = () => quote(cartModel());

  const api = (url, body) =>
    fetch(url, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: body instanceof FormData ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? (body instanceof FormData ? body : JSON.stringify(body)) : undefined
    }).then((r) =>
      r
        .json()
        .catch(() => null)
        .then((j) => {
          if (!r.ok) {
            const err = new Error('cart ' + r.status);
            err.status = r.status;
            throw err;
          }
          return j;
        })
    );
  const js = (route) => route + '.js';

  let chain = Promise.resolve();
  const queue = (fn) => {
    chain = chain.then(fn).catch(() => {
      notify('error', T('error'));
      return refresh();
    });
    return chain;
  };

  function setCart(c) {
    if (c && Array.isArray(c.items)) {
      cart = c;
      render();
      refreshCartPage();
      document.dispatchEvent(new CustomEvent('tenchi:cart', { detail: c }));
    }
    return cart;
  }
  const refresh = () => api(js(R.cart)).then(setCart, () => cart);

  /** Sets the loose (unbundled) quantity of a variant. Caps low-stock variants and says so, without a count. */
  function setQty(vid, target, opts = {}) {
    let q = Math.max(0, Math.floor(Number(target) || 0));
    const limit = capAdd(vid) + qtyOf(vid);
    const before = qtyOf(vid);
    let capped = false;
    if (q > limit) {
      q = Math.max(before, limit);
      capped = true;
    }
    if (q !== looseQty(vid) || want.has(vid)) want.set(vid, q);
    if (capped && !opts.quiet) limitNotice(vid);
    else if (opts.announce && q > before) notify('info', T('added', { n: q - before, label: opts.label || labelOf(vid) }));
    render();
    syncSoon(opts.now ? 0 : 350);
    return q;
  }

  const syncSoon = (() => {
    let t;
    return (ms) => {
      clearTimeout(t);
      t = setTimeout(() => queue(sync), ms);
    };
  })();

  async function sync() {
    if (!want.size) return;
    const snap = new Map(want);
    const updates = {};
    const adds = [];
    snap.forEach((target, vid) => {
      busy.add(vid);
      const loose = cart.items.filter((it) => it.variant_id === vid && isLoose(it));
      const cur = loose.reduce((n, it) => n + it.quantity, 0);
      if (target > cur) {
        if (loose.length) updates[loose[0].key] = loose[0].quantity + target - cur;
        else adds.push({ id: vid, quantity: target - cur });
      } else if (target < cur) {
        let drop = cur - target;
        loose
          .slice()
          .reverse()
          .forEach((it) => {
            if (drop <= 0) return;
            const take = Math.min(drop, it.quantity);
            updates[it.key] = it.quantity - take;
            drop -= take;
          });
      }
    });
    renderSteppers();
    let failed = null;
    try {
      if (Object.keys(updates).length) await api(js(R.update), { updates });
    } catch (e) {
      failed = e;
    }
    if (adds.length) {
      try {
        await api(js(R.add), { items: adds });
      } catch (e) {
        failed = failed || e;
      }
    }
    snap.forEach((t, vid) => {
      if (want.get(vid) === t) want.delete(vid);
      busy.delete(vid);
    });
    await refresh();
    let short = null;
    snap.forEach((t, vid) => {
      if (short == null && !want.has(vid) && looseQty(vid) < t) short = vid;
    });
    if (short != null) limitNotice(short);
    else if (failed && failed.status !== 422) notify('error', T('error'));
  }

  /** Waits until every pending quantity change has reached Shopify. */
  function flushNow() {
    if (want.size) queue(sync);
    return chain.then(() => (want.size ? flushNow() : cart));
  }

  const merge = (lines) => {
    const m = new Map();
    lines.forEach((l) => {
      const id = Number(l.id);
      const q = Number(l.qty) || 0;
      if (id && q > 0) m.set(id, (m.get(id) || 0) + q);
    });
    return Array.from(m).map(([id, qty]) => ({ id, qty }));
  };

  /** Adds loose lines (reorders, pasted lists, packs while offers are off). */
  function addLoose(lines, name) {
    let short = null;
    let added = 0;
    merge(lines).forEach((l) => {
      const before = qtyOf(l.id);
      const got = setQty(l.id, before + l.qty, { quiet: true });
      if (got < before + l.qty && short == null) short = l.id;
      added += got - before;
    });
    syncSoon(0);
    if (short != null) limitNotice(short);
    else if (added) notify('info', T('added_named', { name }));
    return added;
  }

  /** Adds a fixed-price pack or class kit. All its lines carry the same _bundle_id so the tray can check it stays whole. */
  function addBundle(name, lines, price, offerKey) {
    const m = merge(lines);
    if (!m.length) return;
    const shortId = m.find((l) => capAdd(l.id) < l.qty);
    if (shortId) {
      notify('limit', T('bundle_short', { name, label: labelOf(shortId.id) }));
      return;
    }
    const id = 'b' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    const n = m.reduce((s, l) => s + l.qty, 0);
    const key = offerKey || 'packs_bundles';
    const items = m.map((l) => ({
      id: l.id,
      quantity: l.qty,
      properties: { _bundle: name, _bundle_id: id, _bundle_price: String(price), _bundle_n: String(n), _bundle_q: String(l.qty), _offer_key: key }
    }));
    queue(async () => {
      try {
        await api(js(R.add), { items });
        notify('info', T('added_named', { name }));
      } catch (e) {
        await refresh();
        const left = {};
        cart.items.forEach((it) => {
          if (props(it)._bundle_id === id) left[it.key] = 0;
        });
        if (Object.keys(left).length) await api(js(R.update), { updates: left }).catch(() => null);
        notify(e.status === 422 ? 'limit' : 'error', e.status === 422 ? T('bundle_short_any', { name }) : T('error'));
      }
      await refresh();
    });
  }

  function sampleProduct(root) {
    const el = $('[data-tc-sample-json]', root);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }
  function variantForSize(product, size) {
    const n = (s) => String(s || '').replace(/\u00d7/g, 'x').replace(/\s+/g, ' ').trim();
    const want = n(size);
    return ((product && product.variants) || []).find((v) => n(v.option1) === want || n((v.options || [])[0]) === want || n(v.title) === want);
  }
  function samplePick(root) {
    const chip = $('[data-tc-sample-lens]:not([hidden]) [role="radio"][aria-checked="true"]', root);
    const product = sampleProduct(root);
    const found = chip && variantForSize(product, chip.dataset.size);
    const vid = found ? found.id : chip && Number(chip.dataset.vid);
    const idInp = $('[data-tc-sample-id]', root);
    const go = $('[data-tc-sample-go]', root);
    if (idInp) idInp.value = vid || '';
    if (go) go.disabled = !vid || !rxConfirmed();
    return vid ? Number(vid) : 0;
  }
  function goSample(root) {
    const vid = samplePick(root);
    if (!vid) return;
    if (!rxConfirmed()) {
      notify('error', T('rx_need'));
      const box = $('[data-tc-rx]', root);
      if (box) box.focus();
      return;
    }
    const btn = $('[data-tc-sample-go]', root);
    if (btn) btn.disabled = true;
    queue(async () => {
      const existing = cart.items.filter(isSample);
      try {
        if (!(existing.some((it) => it.variant_id === vid && it.quantity >= 1))) {
          if (existing.length) {
            const updates = {};
            existing.forEach((it) => {
              updates[it.key] = 0;
            });
            await api(js(R.update), { updates });
          }
          await api(js(R.add), { items: [{ id: vid, quantity: 1, properties: { _offer: 'free-sample' } }] });
        }
        location.href = checkoutHref();
      } catch (e) {
        if (btn) btn.disabled = false;
        throw e;
      }
    });
  }

  /** Lines that are not plain loose boxes (pack items, sample, plans) change by line key through change.js. */
  const kwant = new Map();
  const keyQty = (it) => (kwant.has(it.key) ? kwant.get(it.key) : it.quantity);
  const sendKey = (() => {
    const timers = new Map();
    return (key) => {
      clearTimeout(timers.get(key));
      timers.set(
        key,
        setTimeout(() => {
          timers.delete(key);
          queue(async () => {
            if (!kwant.has(key)) return;
            const q = kwant.get(key);
            const it = cart.items.find((x) => x.key === key);
            try {
              const c = await api(js(R.change), { id: key, quantity: q });
              if (kwant.get(key) === q) kwant.delete(key);
              setCart(c);
            } catch (e) {
              kwant.delete(key);
              if (e.status === 422) notify('limit', T('limited', { label: it ? labelOf(it.variant_id, it) : '' }));
              else notify('error', T('error'));
              await refresh();
            }
          });
        }, 300)
      );
    };
  })();
  function setKeyQty(key, target) {
    const it = cart.items.find((x) => x.key === key);
    if (!it) return;
    const max = isSample(it) ? 1 : Infinity;
    const q = Math.max(0, Math.min(max, Math.floor(Number(target) || 0)));
    if (q === keyQty(it)) return render();
    kwant.set(key, q);
    render();
    sendKey(key);
  }
  function keyStepperHTML(it, label) {
    const q = keyQty(it);
    const max = isSample(it) ? 1 : 0;
    return (
      `<div class="stp stp--c stp--on" data-tc-kstp data-key="${esc(it.key)}">` +
      `<div class="stp__row" role="group" aria-label="${esc(T('qty_group', { label }))}">` +
      `<button type="button" class="stp__b" data-tc-kdec aria-label="${esc(T('remove_one', { label }))}">&minus;</button>` +
      `<input class="stp__i mono" data-tc-kqty inputmode="numeric" pattern="[0-9]*" value="${q}" aria-label="${esc(T('qty_of', { label }))}">` +
      `<button type="button" class="stp__b" data-tc-kinc aria-label="${esc(T('add_one', { label }))}"${max && q >= max ? ' disabled' : ''}>+</button></div></div>`
    );
  }
  on('click', '[data-tc-kdec],[data-tc-kinc]', (e, b) => {
    const el = b.closest('[data-tc-kstp]');
    const it = cart.items.find((x) => x.key === el.dataset.key);
    if (!it) return;
    setKeyQty(it.key, keyQty(it) + (b.matches('[data-tc-kdec]') ? -1 : 1));
  });
  on('change', '[data-tc-kqty]', (e, inp) => {
    const el = inp.closest('[data-tc-kstp]');
    const it = cart.items.find((x) => x.key === el.dataset.key);
    const raw = inp.value.trim();
    if (!it) return;
    if (!/^\d+$/.test(raw)) {
      inp.value = keyQty(it);
      return;
    }
    setKeyQty(it.key, parseInt(raw, 10));
  });
  on('keydown', '[data-tc-kqty]', (e, inp) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    inp.dispatchEvent(new Event('change', { bubbles: true }));
  });
  function renderKeySteppers() {
    $$('[data-tc-kstp]').forEach((el) => {
      const it = cart.items.find((x) => x.key === el.dataset.key);
      if (!it) return;
      const q = keyQty(it);
      const inp = $('[data-tc-kqty]', el);
      if (inp && document.activeElement !== inp) inp.value = q;
      const inc = $('[data-tc-kinc]', el);
      if (inc) inc.disabled = isSample(it) && q >= 1;
      el.classList.toggle('is-busy', kwant.has(it.key));
    });
  }

  function removeKeys(keys) {
    const updates = {};
    keys.forEach((k) => {
      updates[k] = 0;
    });
    return queue(() => api(js(R.update), { updates }).then(setCart));
  }

  function rxBoxHTML(id) {
    const on = rxConfirmed();
    const at = ((cart.attributes || {})[RX_AT] || '').toString();
    return (
      `<div class="rx" data-tc-rx-box>` +
      `<input type="hidden" name="attributes[${esc(RX_KEY)}]" value="${on ? 'Yes' : ''}" data-tc-rx-val>` +
      `<input type="hidden" name="attributes[${esc(RX_AT)}]" value="${esc(at)}" data-tc-rx-at>` +
      `<label class="rx__lab" for="${esc(id)}"><input id="${esc(id)}" type="checkbox" data-tc-rx${on ? ' checked' : ''}><span>${esc(T('rx_confirm'))}</span></label>` +
      `<p class="legal mono rx__only">${esc(T('rx_only'))}</p></div>`
    );
  }
  function renderRx() {
    const on = rxConfirmed();
    const at = ((cart.attributes || {})[RX_AT] || '').toString();
    $$('[data-tc-rx]').forEach((box) => {
      if (document.activeElement !== box) box.checked = on;
    });
    $$('[data-tc-rx-val]').forEach((inp) => {
      inp.value = on ? 'Yes' : '';
    });
    $$('[data-tc-rx-at]').forEach((inp) => {
      inp.value = at;
    });
    $$('[name="checkout"]').forEach((b) => {
      b.disabled = !on;
      b.setAttribute('aria-disabled', on ? 'false' : 'true');
    });
    $$('[data-tc-dyn-co]').forEach((el) => {
      el.hidden = !on;
    });
    $$('[data-tc-sample-page]').forEach((root) => samplePick(root));
  }
  function setRx(on) {
    const yes = !!on;
    const at = yes ? new Date().toISOString() : '';
    const attrs = Object.assign({}, cart.attributes, { [RX_KEY]: yes ? 'Yes' : '', [RX_AT]: at });
    cart.attributes = attrs;
    renderRx();
    queue(() => api(js(R.update), { attributes: { [RX_KEY]: yes ? 'Yes' : '', [RX_AT]: at } }).then(setCart));
  }
  on('change', '[data-tc-rx]', (e, box) => {
    setRx(box.checked);
  });


  /** PO number: cart attribute "PO number", plus a "PO: x" note unless the buyer wrote their own note. */
  function setPo(value) {
    const po = String(value || '').trim().slice(0, 40);
    if (po === cartPo()) return;
    const note = cart.note || '';
    const body = { attributes: { 'PO number': po } };
    if (!note || /^PO:/.test(note)) body.note = po ? `PO: ${po}` : '';
    cart.attributes = Object.assign({}, cart.attributes, { 'PO number': po });
    renderPo();
    queue(() => api(js(R.update), body).then(setCart));
  }

  /* ---------- Notices (one at a time) ---------- */

  let noticeTimer;
  function notify(kind, text) {
    const box = $('[data-tc-notices]');
    if (!box || !text) return;
    // The open drawer already shows the change and inline stepper messages; only errors still need the live region.
    if (isOpen && kind !== 'error') return;
    box.textContent = '';
    const n = document.createElement('div');
    n.className = 'notice notice--' + kind;
    const s = document.createElement('span');
    s.textContent = text;
    const b = document.createElement('button');
    b.type = 'button';
    b.setAttribute('aria-label', T('dismiss'));
    b.textContent = '\u00d7';
    b.addEventListener('click', () => n.remove());
    n.append(s, b);
    box.append(n);
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(() => n.remove(), kind === 'info' ? 4000 : 9000);
  }
  const msgTimers = new Map();
  function limitNotice(vid) {
    const label = labelOf(vid);
    const out = BY.get(vid) && BY.get(vid).stock === 'out';
    notify('limit', out ? T('sold_out_label', { label }) : T('limited', { label }));
    $$(`[data-tc-stp][data-vid="${vid}"] [data-tc-msg]`).forEach((p) => {
      p.textContent = out ? T('sold_out') : T('limited_short');
      p.hidden = false;
      clearTimeout(msgTimers.get(p));
      msgTimers.set(
        p,
        setTimeout(() => {
          p.hidden = true;
        }, 6000)
      );
    });
    $$(`[data-tc-qi][data-vid="${vid}"]`).forEach((input) => {
      const p = (input.getAttribute('aria-describedby') && document.getElementById(input.getAttribute('aria-describedby'))) || (input.parentElement && $('.err', input.parentElement));
      if (!p) return;
      p.textContent = out ? T('sold_out') : T('limited_short');
      clearTimeout(msgTimers.get(p));
      msgTimers.set(
        p,
        setTimeout(() => {
          p.textContent = '';
        }, 6000)
      );
    });
  }

  /* ---------- Steppers ---------- */

  function stepperHTML(vid, label, compact) {
    const q = qtyOf(vid);
    const s = BY.get(vid) || {};
    const act = T('add');
    const out = s.stock === 'out';
    return (
      `<div class="stp${compact ? ' stp--c' : ''}${q > 0 ? ' stp--on' : ''}" data-tc-stp data-vid="${vid}" data-label="${esc(label)}" data-add-label="${esc(act)}">` +
      `<button type="button" class="btn btn--add" data-tc-add aria-label="${esc(T('add_first', { action: act, label }))}"${out ? ' disabled' : ''}${q > 0 ? ' hidden' : ''}>${out ? esc(T('sold_out')) : `+ <span>${esc(act)}</span>`}</button>` +
      `<div class="stp__row" role="group" aria-label="${esc(T('qty_group', { label }))}" data-tc-row${q === 0 ? ' hidden' : ''}>` +
      `<button type="button" class="stp__b" data-tc-dec aria-label="${esc(T('remove_one', { label }))}">&minus;</button>` +
      `<input class="stp__i mono" data-tc-qty inputmode="numeric" pattern="[0-9]*" value="${q}" aria-label="${esc(T('qty_of', { label }))}">` +
      `<button type="button" class="stp__b" data-tc-inc aria-label="${esc(T('add_one', { label }))}">+</button></div>` +
      `<p class="stp__m mono" data-tc-msg role="status" hidden></p>` +
      (s.stock === 'low' ? `<p class="stp__m stp__m--low mono" data-tc-low>${esc(T('low_stock'))}</p>` : '') +
      `</div>`
    );
  }

  function updateStepper(el) {
    const vid = Number(el.dataset.vid);
    const q = qtyOf(vid);
    const add = $('[data-tc-add]', el);
    const row = $('[data-tc-row]', el);
    const inp = $('[data-tc-qty]', el);
    const a = document.activeElement;
    const rowFocus = row && row.contains(a);
    el.classList.toggle('stp--on', q > 0);
    el.classList.toggle('is-busy', busy.has(vid));
    if (add) add.hidden = q > 0;
    if (row) row.hidden = q === 0;
    if (inp && !(a === inp && inp.dataset.dirty)) inp.value = q;
    if (q === 0 && rowFocus && add) add.focus();
    if (q > 0 && add && a === add) {
      const inc = $('[data-tc-inc]', el);
      if (inc) inc.focus();
    }
  }
  const renderSteppers = () => $$('[data-tc-stp]').forEach(updateStepper);

  /** Points a stepper at another variant (gauge row chips, product page lengths). */
  function relabelStepper(el, vid, label, stock) {
    if (!el) return;
    el.dataset.vid = vid;
    el.dataset.label = label;
    const act = el.dataset.addLabel || T('add');
    const add = $('[data-tc-add]', el);
    const set = (sel, attr, v) => {
      const x = $(sel, el);
      if (x) x.setAttribute(attr, v);
    };
    if (add) {
      add.setAttribute('aria-label', T('add_first', { action: act, label }));
      add.disabled = stock === 'out';
      add.innerHTML = stock === 'out' ? esc(T('sold_out')) : `+ <span>${esc(act)}</span>`;
    }
    set('[data-tc-row]', 'aria-label', T('qty_group', { label }));
    set('[data-tc-dec]', 'aria-label', T('remove_one', { label }));
    set('[data-tc-qty]', 'aria-label', T('qty_of', { label }));
    set('[data-tc-inc]', 'aria-label', T('add_one', { label }));
    const msg = $('[data-tc-msg]', el);
    if (msg) msg.hidden = true;
    let low = $('[data-tc-low]', el);
    if (stock === 'low' && !low && !('noLow' in el.dataset)) {
      low = document.createElement('p');
      low.className = 'stp__m stp__m--low mono';
      low.setAttribute('data-tc-low', '');
      low.textContent = T('low_stock');
      el.append(low);
    } else if (stock !== 'low' && low) low.remove();
    updateStepper(el);
  }

  /** Product page: a chosen subscription plan or app line properties go through the product form. */
  const pdpExtra = (form) => {
    if (!form) return false;
    const fd = new FormData(form);
    if (fd.get('selling_plan')) return true;
    for (const [k, v] of fd.entries()) if (k.indexOf('properties[') === 0 && v !== '') return true;
    return false;
  };
  function addForm(form, qty, label) {
    const fd = new FormData(form);
    fd.set('quantity', String(qty));
    queue(async () => {
      try {
        await api(js(R.add), fd);
        notify('info', T('added', { n: qty, label }));
      } catch (e) {
        if (e.status === 422) notify('limit', T('limited', { label }));
        else notify('error', T('error'));
      }
      await refresh();
    });
  }

  on('click', '[data-tc-add],[data-tc-inc],[data-tc-dec]', (e, b) => {
    const el = b.closest('[data-tc-stp]');
    if (!el) return;
    const vid = Number(el.dataset.vid);
    const label = el.dataset.label;
    const form = b.closest('[data-tc-pdp-form]');
    const dec = b.matches('[data-tc-dec]');
    if (!dec && pdpExtra(form)) {
      addForm(form, 1, label);
      return;
    }
    setQty(vid, qtyOf(vid) + (dec ? -1 : 1), { announce: b.matches('[data-tc-add]'), label });
  });
  on('input', '[data-tc-qty]', (e, inp) => {
    inp.dataset.dirty = '1';
  });
  on('change', '[data-tc-qty]', (e, inp) => {
    delete inp.dataset.dirty;
    const el = inp.closest('[data-tc-stp]');
    const raw = inp.value.trim();
    if (!/^\d+$/.test(raw)) {
      updateStepper(el);
      return;
    }
    setQty(Number(el.dataset.vid), parseInt(raw, 10), { label: el.dataset.label });
  });
  on('keydown', '[data-tc-qty],[data-tc-qi],[data-tc-students]', (e, inp) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      inp.blur();
      inp.focus();
    }
  });
  on('focusout', '[data-tc-qty]', (e, inp) => {
    if (inp.dataset.dirty) inp.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Radio groups: arrow keys and roving focus ---------- */

  const radiosOf = (g) => $$('[role="radio"]', g).filter((r) => r.closest('[role="radiogroup"]') === g && !r.disabled && !r.closest('[hidden]'));
  function checkRadio(btn) {
    const g = btn.closest('[role="radiogroup"]');
    if (!g) return;
    $$('[role="radio"]', g).forEach((r) => {
      if (r.closest('[role="radiogroup"]') !== g) return;
      r.setAttribute('aria-checked', String(r === btn));
      r.tabIndex = r === btn ? 0 : -1;
    });
  }
  function rove(g) {
    const rs = $$('[role="radio"]', g).filter((r) => r.closest('[role="radiogroup"]') === g);
    if (!rs.length) return;
    const cur = rs.find((r) => r.getAttribute('aria-checked') === 'true' && !r.disabled) || rs.find((r) => !r.disabled) || rs[0];
    rs.forEach((r) => {
      r.tabIndex = r === cur ? 0 : -1;
    });
  }
  on('keydown', '[role="radio"]', (e, r) => {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1, Home: 'first', End: 'last' };
    if (!(e.key in keys)) return;
    const g = r.closest('[role="radiogroup"]');
    const rs = radiosOf(g);
    if (!rs.length) return;
    e.preventDefault();
    const i = rs.indexOf(r);
    const k = keys[e.key];
    const next = k === 'first' ? rs[0] : k === 'last' ? rs[rs.length - 1] : rs[(i + k + rs.length) % rs.length];
    next.focus();
    next.click();
  });
  on('keydown', 'a[role="radio"]', (e, r) => {
    if (e.key === ' ') {
      e.preventDefault();
      r.click();
    }
  });

  /* ---------- Gauge rows ---------- */

  on('click', '[data-tc-gauge] .seg__b', (e, b) => {
    checkRadio(b);
    const row = b.closest('[data-tc-gauge]');
    if (b.dataset.len) row.style.setProperty('--len', b.dataset.len);
    relabelStepper($('[data-tc-stp]', row), Number(b.dataset.vid), b.dataset.label, b.dataset.stock);
  });
  function renderGauges() {
    $$('[data-tc-gauge]').forEach((row) => {
      const pieces = [];
      $$('.seg__b', row).forEach((c) => {
        const q = qtyOf(Number(c.dataset.vid));
        let badge = $('.seg__q', c);
        if (q > 0) {
          if (!badge) {
            badge = document.createElement('b');
            badge.className = 'seg__q';
            badge.setAttribute('aria-hidden', 'true');
            c.append(badge);
          }
          badge.textContent = q;
          pieces.push(T('in_piece', { len: c.dataset.len, qty: q }));
        } else if (badge) badge.remove();
        c.setAttribute('aria-label', q > 0 ? T('chip_in_tray', { len: c.dataset.len, qty: q }) : T('chip', { len: c.dataset.len }));
      });
      row.classList.toggle('rule__r--in', pieces.length > 0);
      const p = $('[data-tc-in]', row);
      if (!p) return;
      const html = pieces.length ? esc(T('in_tray', { list: pieces.join(' \u00b7 ') })) : `<span class="rule__len">${esc(row.dataset.empty)}</span>`;
      if (p.innerHTML !== html) p.innerHTML = html;
    });
  }

  /* ---------- Shop: matrix, by length, by gauge ---------- */

  on('click', '[data-tc-plus]', (e, b) => {
    const vid = Number(b.dataset.vid);
    setQty(vid, qtyOf(vid) + 1, { announce: true, label: b.dataset.label });
  });
  on('click', '[data-tc-minus]', (e, b) => {
    const vid = Number(b.dataset.vid);
    setQty(vid, qtyOf(vid) - 1);
    if (qtyOf(vid) === 0) {
      const plus = $('[data-tc-plus]', b.parentElement);
      if (plus) plus.focus();
    }
  });
  function renderMatrix() {
    $$('[data-tc-minus]').forEach((b) => {
      const off = qtyOf(Number(b.dataset.vid)) === 0;
      b.disabled = off;
      b.classList.toggle('is-off', off);
    });
    $$('[data-tc-plus]').forEach((b) => {
      const q = qtyOf(Number(b.dataset.vid));
      let badge = $('.mxq', b);
      if (q > 0) {
        if (!badge) {
          badge = document.createElement('b');
          badge.className = 'mxq';
          badge.setAttribute('aria-hidden', 'true');
          b.append(badge);
        }
        badge.textContent = q;
      } else if (badge) badge.remove();
      b.setAttribute('aria-label', q > 0 ? T('add_box_in_tray', { label: b.dataset.label, qty: q }) : T('add_box', { label: b.dataset.label }));
    });
  }

  function initShop(root) {
    const btns = $$('[data-view]', root);
    const panels = $$('[data-panel]', root);
    const stat = $('[data-tc-shop-stat]', root);
    const chips = $$('[data-panel="length"] [data-len]', root);
    const lists = $$('[data-len-list]', root);
    const desk = matchMedia('(min-width:1024px)');
    const total = Number(root.dataset.total) || 0;
    let view = null;
    const current = () => view || 'gauge';
    const curLen = () => {
      const c = chips.find((x) => x.getAttribute('aria-checked') === 'true');
      return c ? c.dataset.len : '';
    };
    const url = () => {
      const u = new URL(location.href);
      if (view) u.searchParams.set('view', view);
      else u.searchParams.delete('view');
      if (current() === 'length' && curLen()) u.searchParams.set('l', curLen());
      else u.searchParams.delete('l');
      history.replaceState(history.state, '', u);
    };
    const renderStat = () => {
      if (!stat) return;
      const v = current();
      if (v === 'length') {
        const c = chips.find((x) => x.getAttribute('aria-checked') === 'true');
        stat.textContent = c ? P('stat_len', Number(c.dataset.count), { len: c.dataset.len }) : '';
      } else if (v === 'gauge') stat.textContent = T('stat_gauge', { d: $$('[data-panel="gauge"] .gauge-sec', root).length, n: total });
      else stat.textContent = P('stat_matrix', total);
    };
    const pressed = () => btns.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.view === current())));
    const setView = (v) => {
      if (v === 'matrix' && !desk.matches) v = 'gauge';
      view = v;
      panels.forEach((p) => {
        p.hidden = p.dataset.panel !== v;
        p.classList.remove('only-m', 'only-d');
      });
      pressed();
      renderStat();
    };
    const pickLen = (len) => {
      const c = chips.find((x) => x.dataset.len === String(len));
      if (!c) return false;
      checkRadio(c);
      lists.forEach((l) => {
        l.hidden = l.dataset.lenList !== c.dataset.len;
      });
      renderStat();
      return true;
    };
    btns.forEach((b) =>
      b.addEventListener('click', () => {
        setView(b.dataset.view);
        url();
      })
    );
    chips.forEach((c) =>
      c.addEventListener('click', () => {
        pickLen(c.dataset.len);
        url();
      })
    );
    desk.addEventListener('change', () => {
      if (view === 'matrix' && !desk.matches) setView('gauge');
      pressed();
      renderStat();
    });
    const q = new URLSearchParams(location.search);
    const hasLen = q.get('l') && pickLen(q.get('l'));
    const v = q.get('view');
    if (v === 'matrix' || v === 'length' || v === 'gauge') setView(v);
    else if (hasLen) setView('length');
    else setView('gauge');
  }

  /* ---------- Product page ---------- */

  let pdp = null;
  function renderPdpBar() {
    if (!pdp) return;
    const v = pdp.cur();
    if (!v || v.stock === 'out') return setBarQuiet(null);
    const k = qtyOf(v.id);
    const add = () => {
      if (pdpExtra(pdp.form)) addForm(pdp.form, 1, v.label);
      else setQty(v.id, qtyOf(v.id) + 1, { announce: true, label: v.label });
    };
    const price = money(v.price);
    if (k > 0 && !pdpExtra(pdp.form)) return setBarQuiet({ stepper: { vid: v.id, label: v.label }, hint: T('bar_more_hint', { n: k }) });
    setBarQuiet(
      k > 0
        ? { label: T('bar_more', { n: k }), short: T('bar_more_short'), shortHint: T('bar_more_hint', { n: k }), onClick: add }
        : { label: pdp.noun ? T('bar_add', { noun: pdp.noun, price }) : T('bar_add_any', { price }), onClick: add }
    );
  }

  function initPdp(root) {
    let J;
    try {
      J = JSON.parse($('[data-tc-pdp-json]', root).textContent);
    } catch (e) {
      return;
    }
    const form = $('[data-tc-pdp-form]', root);
    if (!form) return;
    const idInput = $('[data-tc-vid]', form);
    const stp = $('[data-tc-stp]', form);
    const track = $('[data-tc-gal-track]', root);
    const slides = $$('[data-tc-gal-slide]', root);
    const thumbs = $$('[data-tc-gal-th] button', root);
    const packSlide = $('[data-tc-gal-slide][data-kind="packshot"]', root);
    const packImg = packSlide && packSlide.querySelector('img');
    const packThumb = thumbs.find((t) => t.dataset.kind === 'packshot');
    let cur = J.variants.find((v) => String(v.id) === String(idInput && idInput.value)) || J.variants[0];
    if (!pdp) pdp = { cur: () => cur, noun: root.dataset.noun || '', form };

    const press = (i) => {
      thumbs.forEach((t) => t.setAttribute('aria-pressed', String(Number(t.dataset.i) === i)));
    };
    const showSlide = (i, instant) => {
      const slide = slides.find((s) => Number(s.dataset.i) === i) || slides[i];
      if (!slide) return;
      slide.scrollIntoView({ inline: 'start', block: 'nearest', behavior: instant ? 'auto' : 'smooth' });
      press(Number(slide.dataset.i));
    };
    const setImg = (img, src) => {
      if (!img || !src) return;
      img.src = src;
      img.removeAttribute('srcset');
      const pic = img.closest('picture');
      if (pic) $$('source', pic).forEach((s) => s.removeAttribute('srcset'));
    };
    /** Length/variant only swaps the leading packshot. Shopify media slides stay in the rail. */
    const setPackshot = (v) => {
      if (!v || !v.img) return;
      setImg(packImg, v.img);
      if (packSlide) packSlide.dataset.vid = String(v.id);
      if (packThumb) {
        setImg(packThumb.querySelector('img'), v.img);
        packThumb.dataset.vid = String(v.id);
      }
    };
    if (track && slides.length > 1 && typeof IntersectionObserver === 'function') {
      const io = new IntersectionObserver(
        (ents) => {
          const on = ents.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (on) press(Number(on.target.dataset.i));
        },
        { root: track, threshold: 0.55 }
      );
      slides.forEach((s) => io.observe(s));
    }
    const select = (vid) => {
      const v = J.variants.find((x) => x.id === vid);
      if (!v) return;
      cur = v;
      if (idInput) {
        idInput.value = v.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const chip = $(`[role="radio"][data-vid="${v.id}"]`, form);
      if (chip) checkRadio(chip);
      relabelStepper(stp, v.id, v.label, v.stock);
      const set = (sel, text) => {
        const x = $(sel, root);
        if (x) x.textContent = text;
      };
      set('[data-tc-pdp-price]', v.priceText);
      set('[data-tc-pdp-title]', v.title);
      set('[data-tc-pdp-stock]', v.stock === 'out' ? T('sold_out') : v.stock === 'low' ? T('low_ships') : T('in_stock_ships'));
      $$('[data-tc-quick]', form).forEach((b) => b.setAttribute('aria-label', T('quick_label', { n: b.dataset.tcQuick, label: v.label })));
      if (v.len) root.style.setProperty('--len', v.len);
      setPackshot(v);
      try {
        history.replaceState(history.state, '', v.url);
      } catch (e) {}
      renderPdpBar();
      document.dispatchEvent(new CustomEvent('tenchi:variant', { detail: { variant: v, form } }));
    };

    $$('[role="radio"][data-vid]', form).forEach((a) =>
      a.addEventListener('click', (e) => {
        e.preventDefault();
        select(Number(a.dataset.vid));
      })
    );
    thumbs.forEach((t) =>
      t.addEventListener('click', () => {
        showSlide(Number(t.dataset.i), false);
      })
    );
    $$('[data-tc-quick]', form).forEach((b) =>
      b.addEventListener('click', () => {
        const k = Number(b.dataset.tcQuick) || 1;
        if (pdpExtra(form)) addForm(form, k, cur.label);
        else setQty(cur.id, qtyOf(cur.id) + k, { announce: true, label: cur.label });
      })
    );
    form.addEventListener('submit', (e) => {
      if (e.submitter && e.submitter.closest('.buy__dyn')) return;
      e.preventDefault();
      if (pdpExtra(form)) addForm(form, 1, cur.label);
      else setQty(cur.id, qtyOf(cur.id) + 1, { announce: true, label: cur.label });
    });
  }

  /* ---------- Packs ---------- */

  const PK_MAX = 99;
  /** A pack with a fixed price must keep its item count; packs added at list price only need one item. */
  function packState(card) {
    const items = $$('[data-tc-item]', card);
    const have = items.reduce((n, li) => n + Number(li.dataset.qty), 0);
    const n = Number(card.dataset.n) || 0;
    const fixed = (Number(card.dataset.price) || 0) > 0 && shown(card.dataset.offer || 'packs_bundles');
    return { items, have, n, fixed, ok: fixed ? have === n : have > 0 };
  }
  function renderPack(card) {
    const st = packState(card);
    const list = st.items.reduce((n, li) => n + Number(li.dataset.price) * Number(li.dataset.qty), 0);
    const offer = Number(card.dataset.price) || 0;
    const listEl = $('[data-tc-pack-list]', card);
    const saveEl = $('[data-tc-pack-save]', card);
    if (offer > 0) {
      const save = Math.max(0, list - offer);
      const needles = st.items.reduce((n, li) => n + (li.dataset.needle ? Number(li.dataset.qty) : 0), 0);
      const boxEl = $('[data-tc-pack-box]', card);
      const atEl = $('[data-tc-pack-at]', card);
      const totEl = $('[data-tc-pack-total]', card);
      if (needles > 0 && boxEl) {
        const clr = card.dataset.clearance === '1';
        const unit = clr ? Math.round(offer / needles) : perBox(needles);
        const pct = clr ? savePct(LIST_BOX - unit, LIST_BOX) : volumePct(needles);
        const min = !clr && pct ? VOLUME.find((t) => needles >= t.min).min : 0;
        boxEl.textContent = T('pack_per_box', { price: money(unit) });
        if (atEl) atEl.textContent = min ? T('pack_at', { n: String(min) }) : '';
        if (saveEl) saveEl.textContent = pct ? T('pack_off', { pct: String(pct) }) : '';
        if (totEl) totEl.textContent = T('pack_total', { price: money(offer) });
      } else {
        if (listEl) listEl.textContent = money(list);
        if (saveEl) saveEl.textContent = T('pack_save_pct', { price: money(save), pct: savePct(save, list) });
      }
    } else if (listEl) listEl.textContent = money(list);
    st.items.forEach((li) => {
      const q = Number(li.dataset.qty);
      li.classList.toggle('is-zero', q === 0);
      const inp = $('[data-tc-pk-qty]', li);
      if (inp && document.activeElement !== inp) inp.value = q;
      const dec = $('[data-tc-pk-dec]', li);
      if (dec) dec.disabled = q === 0;
      const inc = $('[data-tc-pk-inc]', li);
      if (inc) inc.disabled = q >= PK_MAX;
      const line = $('[data-tc-line]', li);
      if (line) line.textContent = money(Number(li.dataset.price) * q);
    });
    const msg = $('[data-tc-pack-st]', card);
    if (msg) {
      let text;
      if (!st.fixed) text = st.have > 0 ? P('pack_any', st.have) : T('pack_empty');
      else if (st.have === st.n) text = T('pack_ok', { n: st.n });
      else if (st.have < st.n) text = P('pack_under', st.n - st.have, { have: st.have, n: st.n });
      else text = P('pack_over', st.have - st.n, { have: st.have, n: st.n });
      if (msg.textContent !== text) msg.textContent = text;
      msg.classList.toggle('is-bad', !st.ok);
    }
    const add = $('[data-tc-pack-add]', card);
    if (add) {
      add.disabled = !st.ok;
      add.setAttribute('aria-label', P('pack_add', st.have, { name: card.dataset.name }));
    }
    const reset = $('[data-tc-pack-reset]', card);
    if (reset) reset.hidden = st.items.every((li) => li.dataset.vid === li.dataset.def && li.dataset.qty === li.dataset.dq);
  }
  function relabelPackLine(li, label) {
    li.dataset.label = label;
    const set = (sel, key) => {
      const x = $(sel, li);
      if (x) x.setAttribute('aria-label', T(key, { label }));
    };
    set('.stp__row', 'qty_group');
    set('[data-tc-pk-dec]', 'remove_one');
    set('[data-tc-pk-qty]', 'qty_of');
    set('[data-tc-pk-inc]', 'add_one');
  }
  function setPackQty(li, q) {
    li.dataset.qty = String(Math.max(0, Math.min(PK_MAX, q)));
    renderPack(li.closest('[data-tc-pack]'));
  }
  on('click', '[data-tc-pk-dec],[data-tc-pk-inc]', (e, b) => {
    const li = b.closest('[data-tc-item]');
    setPackQty(li, Number(li.dataset.qty) + (b.matches('[data-tc-pk-dec]') ? -1 : 1));
    if (b.disabled) {
      const other = $(b.matches('[data-tc-pk-dec]') ? '[data-tc-pk-inc]' : '[data-tc-pk-dec]', li);
      if (other) other.focus();
    }
  });
  on('change', '[data-tc-pk-qty]', (e, inp) => {
    const li = inp.closest('[data-tc-item]');
    const raw = inp.value.trim();
    if (/^\d+$/.test(raw)) setPackQty(li, parseInt(raw, 10));
    else inp.value = li.dataset.qty;
  });
  on('keydown', '[data-tc-pk-qty]', (e, inp) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      inp.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const li = inp.closest('[data-tc-item]');
      setPackQty(li, Number(li.dataset.qty) + (e.key === 'ArrowUp' ? 1 : -1));
      inp.value = li.dataset.qty;
    }
  });
  on('change', '[data-tc-swap]', (e, sel) => {
    const li = sel.closest('[data-tc-item]');
    const opt = sel.selectedOptions[0];
    li.dataset.vid = sel.value;
    li.dataset.price = opt ? opt.dataset.price : li.dataset.price;
    const s = BY.get(Number(sel.value));
    if (s && s.color) li.style.setProperty('--c', s.color);
    relabelPackLine(li, opt ? opt.textContent.trim() : li.dataset.label);
    renderPack(li.closest('[data-tc-pack]'));
  });
  on('click', '[data-tc-pack-reset]', (e, b) => {
    const card = b.closest('[data-tc-pack]');
    $$('[data-tc-item]', card).forEach((li) => {
      li.dataset.qty = li.dataset.dq;
      const sel = $('[data-tc-swap]', li);
      if (sel && sel.value !== li.dataset.def) {
        sel.value = li.dataset.def;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    renderPack(card);
    const add = $('[data-tc-pack-add]', card);
    if (add) add.focus();
  });
  on('click', '[data-tc-pack-add]', (e, b) => {
    const card = b.closest('[data-tc-pack]');
    if (!packState(card).ok) return;
    const lines = $$('[data-tc-item]', card).map((li) => ({ id: li.dataset.vid, qty: li.dataset.qty }));
    const offer = Number(card.dataset.price) || 0;
    if (offer > 0 && shown(card.dataset.offer || 'packs_bundles')) addBundle(card.dataset.name, lines, offer, card.dataset.offer || 'packs_bundles');
    else addLoose(lines, card.dataset.name);
  });

  /* ---------- Free sample (one per order, no stepper, goes to checkout) ---------- */

  on('click', '[data-tc-sample-page] [data-box]', (e, b) => {
    checkRadio(b);
    const root = b.closest('[data-tc-sample-page]');
    $$('[data-tc-sample-lens]', root).forEach((g) => {
      g.hidden = g.dataset.tcSampleLens !== b.dataset.box;
    });
    const g = $(`[data-tc-sample-lens="${b.dataset.box}"]`, root);
    const pick = g && ($('[role="radio"][aria-checked="true"]:not([disabled])', g) || $('[role="radio"]:not([disabled])', g));
    if (pick) checkRadio(pick);
    samplePick(root);
  });
  on('click', '[data-tc-sample-lens] [role="radio"]', (e, b) => {
    checkRadio(b);
    samplePick(b.closest('[data-tc-sample-page]'));
  });
  on('submit', '[data-tc-sample]', (e, form) => {
    e.preventDefault();
    goSample(form.closest('[data-tc-sample-page]') || form);
  });
  $$('[data-tc-sample-page]').forEach(samplePick);

  /* ---------- Next-case nudges ---------- */

  function renderNext() {
    const els = $$('[data-tc-next]');
    if (!els.length) return;
    const q = cartQuote();
    const nc = q.next;
    const link = `<a href="${esc(R.bulk)}" class="link">${esc(T('build_short'))}</a>`;
    const html =
      nc
        ? `${esc(P('next_case', nc.more, { size: nc.boxes, price: money(nc.price) }))}${tag('volume_ladder')} ${link}${proposed('volume_ladder') ? '. ' + esc(T('list_today')) : ''}`
        : `${link}${proposed('volume_ladder') ? '. ' + esc(T('list_today')) : ''}`;
    els.forEach((el) => {
      if (el.innerHTML !== html) el.innerHTML = html;
    });
  }

  /* ---------- Tray (cart drawer) ---------- */

  const tray = $('[data-tc-tray]');
  const sheet = $('[data-tc-sheet]');
  const scrim = $('[data-tc-scrim]');
  const openLink = $('[data-tc-open]');
  let isOpen = false;
  let opener = null;
  let keep = new Set();
  let lineSig = '';
  let bar = null;

  const linesSig = () => {
    const g = groups();
    return JSON.stringify([
      Array.from(new Set(g.loose.map((it) => it.variant_id).concat(Array.from(want.keys()), Array.from(keep)))).sort(),
      g.bundles.map((b) => [b.id, b.intact, b.items.map((it) => [it.key, it.quantity])]),
      g.sample.map((it) => [it.key, it.quantity]),
      g.fixed.map((it) => [it.key, it.quantity])
    ]);
  };

  const sortLines = (a, b) => {
    const sa = BY.get(a.id);
    const sb = BY.get(b.id);
    const ka = sa ? (sa.kind === 'needle' ? sa.box * 1000 + Number(sa.len) : 9000) : 9999;
    const kb = sb ? (sb.kind === 'needle' ? sb.box * 1000 + Number(sb.len) : 9000) : 9999;
    return ka - kb;
  };

  function lineHTML(id, label, sub, right, price, color) {
    return (
      `<li class="tline"${color ? ` style="--c:${esc(color)}"` : ''}>` +
      `<span class="tline__sw${color ? '' : ' tline__sw--acc'}" aria-hidden="true"></span>` +
      `<span class="tline__n"><b>${esc(label)}</b>${sub ? `<span class="mono">${esc(sub)}</span>` : ''}</span>` +
      right +
      `<span class="tline__p mono">${price}</span></li>`
    );
  }

  function trayLinesHTML() {
    const g = groups();
    let html = '';
    const looseIds = new Map();
    g.loose.forEach((it) => {
      if (!looseIds.has(it.variant_id)) looseIds.set(it.variant_id, it);
    });
    want.forEach((q, vid) => {
      if (!looseIds.has(vid)) looseIds.set(vid, null);
    });
    keep.forEach((vid) => {
      if (!looseIds.has(vid)) looseIds.set(vid, null);
    });
    const loose = Array.from(looseIds).map(([id, it]) => ({ id, it })).sort(sortLines);
    loose.forEach(({ id, it }) => {
      const s = BY.get(id);
      const each = s ? s.price : it ? it.original_price : 0;
      const label = labelOf(id, it);
      html += lineHTML(id, label, T('each', { price: money(each) }), stepperHTML(id, label, true), `<span data-tc-lp="${id}">${mh(each * qtyOf(id))}</span>`, s && s.color);
    });
    g.bundles.forEach((b) => {
      const bl = b.items.reduce((n, it) => n + it.original_line_price, 0);
      const head = b.intact && b.price > 0 && shown(b.offerKey || 'packs_bundles') ? `${esc(b.name)} \u00b7 ${mh(b.price)}${tag(b.offerKey || 'packs_bundles')}` : esc(b.name);
      html +=
        `<li class="tray__grp"><span>${head}${b.intact ? '' : ` \u00b7 ${esc(T('bundle_changed'))}`}</span>` +
        `<button type="button" class="btn--link tap" data-tc-rm-bundle="${esc(b.id)}" aria-label="${esc(T('remove_label', { name: b.name }))}">${esc(T('remove'))}</button></li>`;
      b.items
        .slice()
        .sort((x, y) => sortLines({ id: x.variant_id }, { id: y.variant_id }))
        .forEach((it) => {
          const s = BY.get(it.variant_id);
          const label = labelOf(it.variant_id, it);
          html += lineHTML(it.variant_id, label, T('in_pack', { name: b.name }), keyStepperHTML(it, label), money(it.original_line_price), s && s.color);
        });
      if (!b.intact) {
        const have = b.items.reduce((n, it) => n + it.quantity, 0);
        const count = b.n > 0 && have !== b.n ? (have < b.n ? P('pack_under', b.n - have, { have, n: b.n }) : P('pack_over', have - b.n, { have, n: b.n })) + ' ' : '';
        html += `<li class="tray__fine mono" data-tc-pack-tray>${esc(count + T('bundle_changed_note', { price: money(bl) }))}</li>`;
      }
    });
    g.sample.forEach((it) => {
      html += lineHTML(
        it.variant_id,
        T('sample_line'),
        '',
        `<button type="button" class="btn--link tap" data-tc-line-rm="${esc(it.key)}" aria-label="${esc(T('remove_label', { name: T('sample_line') }))}">${esc(T('remove'))}</button>`,
        '',
        ''
      );
    });
    g.fixed.forEach((it) => {
      const label = labelOf(it.variant_id, it);
      const sub = it.selling_plan_allocation ? it.selling_plan_allocation.selling_plan.name : '';
      html += lineHTML(it.variant_id, label, sub, keyStepperHTML(it, label), money(it.final_line_price), '');
    });
    return html;
  }

  function caseMeterHTML(q) {
    if (!VOL || !LADDER.length || !q.next) return '';
    const n = q.volumeBoxes != null ? q.volumeBoxes : q.boxes;
    const stops = [0].concat(LADDER);
    const seg = 100 / (stops.length - 1);
    const pos = (b) => {
      for (let i = 1; i < stops.length; i++) if (b <= stops[i]) return (i - 1 + (b - stops[i - 1]) / (stops[i] - stops[i - 1])) * seg;
      return 100;
    };
    const now = q.cases.length ? caseText(q.cases) : n < 5 ? T('list_price') : T('loose_list');
    const nc = q.next;
    const ticks = stops
      .slice(1, -1)
      .map((t, i) => `<b style="left:${((i + 1) * seg).toFixed(2)}%"${n >= t ? ' class="on"' : ''}></b>`)
      .join('');
    return (
      `<div class="meter"><div class="meter__t mono"><span>${esc(P('boxes_now', n, { now }))}</span>` +
      `<span class="nw">${esc(T('more_case', { more: nc.more, size: nc.boxes, price: money(nc.price) }))}${tag()}</span></div>` +
      `<div class="meter__bar" role="meter" aria-label="${esc(T('case_progress'))}" aria-valuemin="0" aria-valuemax="${stops[stops.length - 1]}" aria-valuenow="${Math.min(n, stops[stops.length - 1])}" aria-valuetext="${esc(T('case_progress_text', { boxes: n, more: nc.more, size: nc.boxes }))}">` +
      `<i style="width:${pos(n).toFixed(2)}%"></i>${ticks}</div>` +
      `<div class="meter__s mono" aria-hidden="true">${stops.map((s) => `<span>${s}</span>`).join('')}</div></div>`
    );
  }
  function shipMeterHTML(total) {
    if (!FREE) return '';
    const left = Math.max(0, FREE - total);
    const on = left <= 0;
    return (
      `<div class="meter meter--ship${on ? ' is-free' : ''}" data-tc-ship><div class="meter__t mono"><span data-tc-ship-label>${on ? '<span class="ship-ok" aria-hidden="true"></span>' : ''}${esc(on ? T('ship_free') : T('ship_left', { amount: money(left) }))}</span><span>${esc(moneyShort(FREE))}+</span></div>` +
      `<div class="meter__bar" role="meter" aria-label="${esc(T('ship_progress'))}" aria-valuemin="0" aria-valuemax="${FREE / 100}" aria-valuenow="${Math.min(total, FREE) / 100}"><i style="width:${Math.min(100, (total / FREE) * 100).toFixed(2)}%"></i></div></div>`
    );
  }
  const SHIP_KEY = 'tenchi-ship-unlock';
  const SHIP_COLORS = (D.needles || []).map((n) => n.color).filter(Boolean);
  let shipWas = null;
  let shipCele = 0;
  let shipCeleT = 0;
  const shipSess = {
    get() {
      try {
        return sessionStorage.getItem(SHIP_KEY) === '1';
      } catch (e) {
        return false;
      }
    },
    set(v) {
      try {
        if (v) sessionStorage.setItem(SHIP_KEY, '1');
        else sessionStorage.removeItem(SHIP_KEY);
      } catch (e) {}
    }
  };
  function shipBurst(host) {
    if (!host || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (host.querySelector('.ship-burst')) return;
    const box = document.createElement('span');
    box.className = 'ship-burst';
    box.setAttribute('aria-hidden', 'true');
    const n = 12;
    for (let i = 0; i < n; i++) {
      const d = document.createElement('i');
      d.style.setProperty('--c', SHIP_COLORS[i % SHIP_COLORS.length] || '#D31820');
      d.style.setProperty('--a', ((i / n) * 150 - 75).toFixed(1) + 'deg');
      d.style.setProperty('--x', (8 + (i % 5) * 7) + 'px');
      box.append(d);
    }
    host.append(box);
  }
  function paintShipCele() {
    const live = performance.now() < shipCele;
    const hosts = [$('.tray__mid'), ...$$('[data-tc-ship]')].filter(Boolean);
    const labels = [$('[data-tc-hint]'), ...$$('[data-tc-ship-label]')].filter(Boolean);
    hosts.forEach((el) => {
      el.classList.toggle('is-cele', live);
      if (live) shipBurst(el);
      else el.querySelectorAll('.ship-burst').forEach((n) => n.remove());
    });
    labels.forEach((el) => el.classList.toggle('is-cele', live));
  }
  function celebrateShip() {
    shipCele = performance.now() + 1200;
    const live = $('[data-tc-ship-live]');
    if (live) {
      live.textContent = '';
      requestAnimationFrame(() => {
        live.textContent = T('ship_unlocked');
      });
    }
    paintShipCele();
    clearTimeout(shipCeleT);
    shipCeleT = setTimeout(paintShipCele, 1250);
  }
  function syncShipUnlock() {
    if (!FREE) return;
    const above = itemsNow() > 0 && totalNow() >= FREE;
    const first = shipWas === null;
    const crossed = !first && above && !shipWas;
    const dropped = !first && !above && shipWas;
    shipWas = above;
    if (first) {
      shipSess.set(above);
      return;
    }
    if (dropped) {
      shipSess.set(false);
      shipCele = 0;
      const live = $('[data-tc-ship-live]');
      if (live) live.textContent = '';
      paintShipCele();
      return;
    }
    if (crossed) {
      shipSess.set(true);
      celebrateShip();
    } else if (performance.now() < shipCele) paintShipCele();
  }
  function renderCartShip() {
    const el = $('[data-tc-cart-ship]');
    if (el) el.innerHTML = shipMeterHTML(totalNow());
  }
  /** "If approved" block: savings from offers that are still proposed. */
  function propHTML(q) {
    const rows = [];
    (q.bundles || []).forEach((b) => {
      if (proposed(b.offerKey || 'packs_bundles') && b.save > 0) rows.push(`<div><dt>${esc(T('fixed_price', { name: b.name }))}</dt><dd class="mono">${mnh(b.save)}</dd></div>`);
    });
    if (proposed('volume_ladder') && q.save.cases > 0) rows.push(`<div><dt>${esc(T('case_price', { cases: caseText(q.cases) || T('volume_price', { pct: String(q.pct), n: q.volumeBoxes }) }))}</dt><dd class="mono">${mnh(q.save.cases)}</dd></div>`);
    if (proposed('clearance') && q.save.clearance > 0) rows.push(`<div><dt>${esc(T('clearance_price'))}</dt><dd class="mono">${mnh(q.save.clearance)}</dd></div>`);
    if (proposed('sharps_deal') && q.save.sharps > 0) rows.push(`<div><dt>${esc(T('sharps_price', { price: money(D.sharpsWith) }))}</dt><dd class="mono">${mnh(q.save.sharps)}</dd></div>`);
    if (!rows.length) return '';
    let h = `<dl class="sum sum--prop" aria-label="${esc(T('prop_label'))}"><div class="sum__ph"><dt>${esc(T('if_approved'))}${tag('volume_ladder') || tag('packs_bundles') || tag('clearance') || tag('sharps_deal')}</dt><dd class="mono">${esc(T('not_live'))}</dd></div>`;
    return h + rows.join('') + `<div class="sum__pt"><dt>${esc(T('prop_total'))}</dt><dd class="mono">${mh(q.proposed)}</dd></div></dl>`;
  }
  function sumsHTML(q) {
    const total = totalNow();
    let h = `<dl class="sum"><div class="sum__pay"><dt>${esc(T('pay_today'))} <small class="mono">${esc(T('pay_today_note'))}</small></dt><dd class="mono">${mh(total)}</dd></div>`;
    (cart.cart_level_discount_applications || []).forEach((d) => {
      h += `<div><dt>${esc(d.title)}</dt><dd class="mono">${mnh(d.total_allocated_amount)}</dd></div>`;
    });
    h += `<div><dt>${esc(T('shipping'))}</dt><dd class="mono">${esc(FREE && total >= FREE ? T('free') : T('at_checkout'))}</dd></div></dl>`;
    return h + propHTML(q);
  }

  function renderSheet(full) {
    if (!sheet || !isOpen) return;
    const items = itemsNow();
    const empty = items <= 0 && !keep.size;
    const q = cartQuote();
    const hasLines = !!$('[data-tc-sheet-lines]', sheet);
    if (full || hasLines === empty) {
      lineSig = '';
      const hist = store.get(HKEY, []);
      const frame =
        `<div class="tray__top"><h2 id="tray-h" class="tray__h">${esc(T('tray'))} <span class="mono" data-tc-sheet-n></span></h2>` +
        `<button type="button" class="btn btn--ghost" data-tc-close>${esc(T('close'))}</button></div>` +
        (empty
          ? `<div class="tray__empty"><p>${esc(T('empty'))}</p><a href="${esc(R.shop)}" class="btn btn--go btn--block">${esc(T('pick'))}</a>` +
            (Array.isArray(hist) && hist.length && R.reorder ? `<a href="${esc(R.reorder)}" class="btn btn--block mt8">${esc(T('reorder_last'))}</a>` : '') +
            `</div>`
          : `<ul class="tray__lines" data-tc-sheet-lines></ul><div data-tc-sheet-meters></div>` +
            `<label class="po"><span class="mono lbl">${esc(T('po_label'))}</span><input data-tc-po value="${esc(cartPo())}" placeholder="${esc(T('po_placeholder'))}" maxlength="40" autocomplete="off"><span class="po__h mono">${esc(T('po_help'))}</span></label>` +
            `<div data-tc-sheet-sums></div>` +
            rxBoxHTML('tray-rx') +
            `<form action="${esc(R.cart)}" method="post"><button type="submit" name="checkout" class="btn btn--go btn--block" data-tc-sheet-go${rxConfirmed() ? '' : ' disabled'}></button></form>` +
            `<div class="mt8"><button type="button" class="btn btn--block" data-tc-quote="cart" data-tc-quote-tray aria-describedby="quote-tray">${esc(T('quote_button'))}</button><span id="quote-tray" class="sr" role="status"></span></div>`) +
        `<p class="tray__fine mono">${esc(T('fine'))}</p>`;
      if (full) sheet.innerHTML = frame;
      else
        keepFocus(sheet, () => {
          sheet.innerHTML = frame;
        });
    }
    const n = $('[data-tc-sheet-n]', sheet);
    if (n) n.textContent = P('items', Math.max(0, items));
    const ul = $('[data-tc-sheet-lines]', sheet);
    if (!ul) return;
    const sig = linesSig();
    if (sig !== lineSig) {
      lineSig = sig;
      keepFocus(sheet, () => {
        ul.innerHTML = trayLinesHTML();
      });
    } else {
      $$('[data-tc-lp]', ul).forEach((el) => {
        const id = Number(el.dataset.tcLp);
        const s = BY.get(id);
        const it = cart.items.find((x) => x.variant_id === id && isLoose(x));
        el.textContent = money((s ? s.price : it ? it.original_price : 0) * qtyOf(id));
      });
    }
    $('[data-tc-sheet-meters]', sheet).innerHTML = caseMeterHTML(q) + shipMeterHTML(totalNow());
    $('[data-tc-sheet-sums]', sheet).innerHTML = sumsHTML(q);
    const go = $('[data-tc-sheet-go]', sheet);
    if (go) {
      go.textContent = T('checkout_total', { price: money(totalNow()) });
      go.disabled = !rxConfirmed();
      go.setAttribute('aria-disabled', rxConfirmed() ? 'false' : 'true');
    }
    const qb = $('[data-tc-quote-tray]', sheet);
    if (qb) qb.dataset.title = P('quote_order', q.boxes);
  }

  /** Re-renders a container and puts focus back on the same control, or on the dialog if it is gone. */
  function keepFocus(container, fn) {
    const a = document.activeElement;
    let sel = null;
    if (a && container.contains(a) && a !== container) {
      const stp = a.closest('[data-tc-stp]');
      const kstp = a.closest('[data-tc-kstp]');
      if (stp) {
        const part = ['[data-tc-inc]', '[data-tc-dec]', '[data-tc-qty]', '[data-tc-add]'].find((s) => a.matches(s));
        sel = `[data-tc-stp][data-vid="${stp.dataset.vid}"] ${part || '[data-tc-add]'}`;
      } else if (kstp) {
        const part = ['[data-tc-kinc]', '[data-tc-kdec]', '[data-tc-kqty]'].find((s) => a.matches(s));
        sel = `[data-tc-kstp][data-key="${CSS.escape(kstp.dataset.key)}"] ${part || '[data-tc-kqty]'}`;
      } else if (a.dataset.tcRm) sel = `[data-tc-rm="${CSS.escape(a.dataset.tcRm)}"]`;
      else if (a.dataset.tcRmBundle) sel = `[data-tc-rm-bundle="${CSS.escape(a.dataset.tcRmBundle)}"]`;
    }
    fn();
    if (a && container.contains(a)) return;
    if (sel || (a && !document.contains(a))) {
      let el = sel && $(sel, container);
      if (el && el.closest('[hidden]')) el = $(sel.replace(/\[data-tc-(inc|dec|qty)\]$/, '[data-tc-add]'), container);
      if (el && el.disabled) el = $('[data-tc-kqty],[data-tc-qty]', el.parentElement) || el;
      (el && !el.closest('[hidden]') ? el : container).focus();
    }
  }

  function openTray(from) {
    if (!tray || !sheet || isOpen) return;
    isOpen = true;
    opener = from || openLink;
    const notices = $('[data-tc-notices]');
    if (notices) notices.textContent = '';
    keep = new Set(groups().loose.map((it) => it.variant_id).concat(Array.from(want.keys())));
    sheet.hidden = false;
    tray.classList.add('tray--open');
    if (scrim) scrim.hidden = false;
    document.body.classList.add('lock');
    if (openLink) openLink.setAttribute('aria-expanded', 'true');
    renderSheet(true);
    sheet.focus();
  }
  function closeTray() {
    if (!isOpen) return;
    isOpen = false;
    keep = new Set();
    sheet.hidden = true;
    sheet.innerHTML = '';
    tray.classList.remove('tray--open');
    if (scrim) scrim.hidden = true;
    document.body.classList.remove('lock');
    if (openLink) openLink.setAttribute('aria-expanded', 'false');
    renderBar();
    const back = opener && document.contains(opener) && opener.getClientRects().length ? opener : openLink;
    if (back) back.focus();
  }
  on('click', '[data-tc-open]', (e, a) => {
    e.preventDefault();
    openTray(a);
  });
  on('click', '[data-tc-close]', () => closeTray());
  if (scrim) scrim.addEventListener('click', closeTray);
  document.addEventListener('keydown', (e) => {
    if (!isOpen) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      closeTray();
      return;
    }
    if (e.key !== 'Tab') return;
    const f = $$('a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', sheet).filter(
      (x) => !x.closest('[hidden]') && x.getClientRects().length
    );
    if (!f.length) {
      e.preventDefault();
      sheet.focus();
      return;
    }
    const first = f[0];
    const last = f[f.length - 1];
    if (!sheet.contains(document.activeElement) || document.activeElement === sheet) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });
  on('click', '[data-tc-rm]', (e, b) => removeKeys([b.dataset.tcRm]));
  on('click', '[data-tc-rm-bundle]', (e, b) => {
    const id = b.dataset.tcRmBundle;
    removeKeys(cart.items.filter((it) => (props(it)._bundle_id || props(it)._bundle) === id).map((it) => it.key));
  });

  function renderBar() {
    if (!tray) return;
    const boxes = boxesNow();
    const items = itemsNow();
    const total = totalNow();
    motionOnCount(items);
    const set = (sel, v) => {
      const el = $(sel, tray);
      if (el && el.textContent !== String(v)) el.textContent = v;
    };
    set('[data-tc-boxes]', boxes);
    set('[data-tc-boxes-word]', boxes === 1 ? T('box') : T('boxes'));
    set('[data-tc-total]', money(total));
    const mini = $('[data-tc-mini]', tray);
    if (mini) mini.style.width = (FREE ? Math.min(100, (total / FREE) * 100) : 100).toFixed(1) + '%';
    const hintEl = $('[data-tc-hint]', tray);
    if (hintEl) {
      const left = FREE - total;
      const freeNow = items > 0 && left <= 0;
      let html;
      if (freeNow) html = '<span class="ship-ok" aria-hidden="true"></span>' + esc(T('hint_free'));
      else {
        const base = items <= 0 ? T('hint_empty', { amount: moneyShort(FREE) }) : T('hint_left', { amount: money(left) });
        const hint = bar && bar.hint ? bar.hint : base;
        html = bar && bar.shortHint ? `<span class="ph-long">${esc(hint)}</span><span class="ph-short">${esc(bar.shortHint)}</span>` : esc(hint);
      }
      hintEl.classList.toggle('is-free', freeNow);
      if (hintEl.innerHTML !== html) hintEl.innerHTML = html;
    }
    const mid = $('.tray__mid', tray);
    if (mid) mid.classList.toggle('is-free', items > 0 && FREE && total >= FREE);
    if (mini) mini.classList.toggle('is-free', items > 0 && FREE && total >= FREE);
    const wrap = $('[data-tc-cta-wrap]', tray);
    if (!wrap) return;
    if (bar && bar.stepper) {
      const key = `stp:${bar.stepper.vid}`;
      if (wrap.dataset.html !== key) {
        const a = document.activeElement;
        const part = a && wrap.contains(a) && ['[data-tc-inc]', '[data-tc-dec]', '[data-tc-qty]'].find((s) => a.matches(s));
        wrap.innerHTML = `<div class="tray__stp">${stepperHTML(bar.stepper.vid, bar.stepper.label, true)}</div>`;
        wrap.dataset.html = key;
        if (a && !document.contains(a)) {
          const f = $(part || '[data-tc-inc]', wrap);
          if (f) f.focus();
        }
      }
      return;
    }
    let html;
    if (bar) {
      const text = bar.short ? `<span class="ph-long">${esc(bar.label)}</span><span class="ph-short" aria-hidden="true">${esc(bar.short)}</span>` : esc(bar.label);
      html = `<button type="button" class="btn btn--go tray__cta" data-tc-bar-cta${bar.short ? ` aria-label="${esc(bar.label)}"` : ''}${bar.disabled ? ' disabled' : ''}>${text}</button>`;
    } else if (items <= 0) html = `<a class="btn btn--go tray__cta" href="${esc(R.shop)}">${esc(T('pick'))}</a>`;
    else if (!rxConfirmed()) html = `<button type="button" class="btn btn--go tray__cta" data-tc-open-rx>${esc(T('checkout'))}</button>`;
    else html = `<form action="${esc(R.cart)}" method="post"><button type="submit" name="checkout" class="btn btn--go tray__cta">${esc(T('checkout'))}</button></form>`;
    if (wrap.dataset.html !== html) {
      const had = wrap.contains(document.activeElement);
      wrap.innerHTML = html;
      wrap.dataset.html = html;
      if (had) {
        const f = $('a,button', wrap);
        if (f) f.focus();
      }
    }
  }
  on('click', '[data-tc-bar-cta]', () => {
    if (bar && bar.onClick) bar.onClick();
  });
  on('click', '[data-tc-open-rx]', () => {
    const open = $('[data-tc-open]');
    if (open) open.click();
    else location.href = R.cart;
  });
  const setBar = (b) => {
    bar = b;
    renderBar();
  };

  /* ---------- Checkout: wait for pending changes, keep a device copy of the order ---------- */

  function recordHistory() {
    const lines = merge(cart.items.map((it) => ({ id: it.variant_id, qty: it.quantity })));
    if (!lines.length) return;
    const h = store.get(HKEY, []);
    store.set(HKEY, [{ at: new Date().toISOString(), lines, po: cartPo() }].concat(Array.isArray(h) ? h : []).slice(0, 6));
  }
  document.addEventListener('submit', (e) => {
    const form = e.target;
    const btn = e.submitter;
    if (!(btn && btn.name === 'checkout')) return;
    e.preventDefault();
    if (!rxConfirmed()) {
      notify('error', T('rx_need'));
      const box = $('[data-tc-rx]', form) || $('[data-tc-rx]');
      if (box) box.focus();
      else {
        const open = $('[data-tc-open]');
        if (open) open.click();
      }
      return;
    }
    if (form.dataset.tcWait) return;
    const trayCheckout = 'tcSheetGo' in btn.dataset;
    form.dataset.tcWait = '1';
    btn.disabled = true;
    const po = $('[data-tc-po]');
    if (po) setPo(po.value);
    const note = $('[data-tc-note]', form);
    if (note && note.value !== cart.note) queue(() => api(js(R.update), { note: note.value }).then(setCart));
    flushNow().then(() => {
      delete form.dataset.tcWait;
      btn.disabled = false;
      const fresh = trayCheckout ? $('[data-tc-sheet-go]') : $('#cart-form [name="checkout"]');
      recordHistory();
      if (fresh && fresh.form) {
        const checkout = document.createElement('input');
        checkout.type = 'hidden';
        checkout.name = 'checkout';
        checkout.value = 'Checkout';
        fresh.form.append(checkout);
        HTMLFormElement.prototype.submit.call(fresh.form);
      } else {
        location.href = R.cart.replace(/\/cart$/, '') + '/checkout';
      }
    });
  });

  /* ---------- PO inputs ---------- */

  const poHelp = new WeakMap();
  function renderPo() {
    const po = cartPo();
    $$('[data-tc-po]').forEach((inp) => {
      if (document.activeElement !== inp && inp.value !== po) inp.value = po;
    });
    $$('[data-tc-po-help]').forEach((h) => {
      if (!poHelp.has(h)) poHelp.set(h, h.textContent);
      h.textContent = po ? T('po_help_set', { po }) : poHelp.get(h);
    });
    $$('[data-tc-po-num]').forEach((inp) => {
      inp.placeholder = po;
    });
  }
  const poLater = debounce((v) => setPo(v), 800);
  on('input', '[data-tc-po]', (e, inp) => poLater(inp.value));
  on('change', '[data-tc-po]', (e, inp) => setPo(inp.value));

  /* ---------- Bulk & Wholesale ---------- */

  const bulk = $('[data-tc-bulk]');
  const CKEY = 'tenchi.case.v1';
  let target = bulk ? Number(bulk.dataset.target) || 100 : 100;
  const pasteSample = '0.22x30 10\n0.25x40 6\n0.16 x 15mm, 4\nsharps 2\n0.18x40 3\n0.35x30 1';

  function renderBulk() {
    if (!bulk) return;
    const boxes = boxesNow();
    const fillN = $('[data-fill-n]', bulk);
    if (fillN) {
      const cs = CASES.find((c) => c.size === target);
      const plan = casePlan(boxes, LIST_BOX);
      const nc = nextCase(boxes);
      const left = target - boxes;
      let status;
      if (boxes === 0) status = T('fill_empty', { target });
      else if (left > 0) status = P('fill_more', left, { target });
      else if (left === 0) status = T('fill_full', { target, price: cs ? money(cs.price) : '' });
      else {
        const planText = caseText(plan.cases) || T('volume_price', { pct: String(plan.pct), n: boxes });
        status = T('fill_over', { over: -left, target, plan: planText });
        if (nc && nc.price <= plan.cost) status += ' ' + T('fill_next_case', { more: nc.more, size: nc.boxes, price: money(nc.price) });
      }
      fillN.textContent = T('fill_n', { boxes, target });
      const st = $('[data-fill-status]', bulk);
      if (st) st.textContent = status;
      const fb = $('[data-fill-bar]', bulk);
      if (fb) {
        fb.setAttribute('aria-valuemax', target);
        fb.setAttribute('aria-valuenow', Math.min(boxes, target));
        fb.setAttribute('aria-label', T('fill_label', { size: target }));
        const i = $('i', fb);
        if (i) i.style.setProperty('--p', Math.min(100, (boxes / target) * 100).toFixed(1));
        const full = boxes === target;
        if (full !== fb.classList.contains('is-full')) fb.classList.toggle('is-full', full);
      }
    }
    $$('[data-tc-qi]', bulk).forEach((inp) => {
      if (document.activeElement === inp) return;
      const q = qtyOf(Number(inp.dataset.vid));
      const v = q > 0 ? String(q) : '';
      if (inp.value !== v && !inp.classList.contains('bad')) inp.value = v;
    });
    const q = cartQuote();
    const sl = $('[data-sum-list]', bulk);
    if (sl) sl.textContent = money(q.list);
    const sp = $('[data-sum-prop]', bulk);
    if (sp) sp.textContent = money(q.proposed);
    const go = $('[data-tc-bulk-checkout]', bulk);
    if (go) {
      const items = itemsNow();
      go.disabled = items <= 0;
      go.textContent = items <= 0 ? T('checkout_empty') : boxes >= (CASES[0] ? CASES[0].size : Infinity) && VOL ? P('checkout_n', boxes) : T('checkout_list');
    }
    if (VOL && CASES.length) {
      setBarQuiet({
        label: T('review_case'),
        short: T('review'),
        hint: T('case_hint', { boxes, target }),
        shortHint: T('fill_n', { boxes, target }),
        onClick: () => openTray($('[data-tc-bar-cta]'))
      });
    }
  }
  let barQuiet = false;
  const setBarQuiet = (b) => {
    bar = b;
    if (!barQuiet) renderBar();
  };

  function initBulk() {
    if (!bulk) return;
    const saved = Number(store.get(CKEY, 0));
    if ($(`.case[data-size="${saved}"]`, bulk)) target = saved;
    const pick = $(`.case[data-size="${target}"]`, bulk);
    if (pick) checkRadio(pick);
    $$('.case[data-size]', bulk).forEach((b) =>
      b.addEventListener('click', () => {
        checkRadio(b);
        target = Number(b.dataset.size);
        store.set(CKEY, target);
        renderBulk();
      })
    );
    const commit = (inp) => {
      const vid = Number(inp.dataset.vid);
      const raw = inp.value.trim();
      const err = document.getElementById('e-' + vid);
      const setErr = (m) => {
        if (err) err.textContent = m;
        inp.classList.toggle('bad', !!m);
        if (m) inp.setAttribute('aria-invalid', 'true');
        else inp.removeAttribute('aria-invalid');
      };
      if (raw === '') {
        setErr('');
        setQty(vid, 0, { quiet: true });
        return;
      }
      if (!/^\d+$/.test(raw)) {
        setErr(T('whole'));
        return;
      }
      const w = parseInt(raw, 10);
      setErr('');
      const got = setQty(vid, w, { quiet: true });
      if (got < w) {
        setErr(BY.get(vid) && BY.get(vid).stock === 'out' ? T('sold_out') : T('limited_short'));
        inp.value = got > 0 ? String(got) : '';
      }
    };
    $$('[data-tc-qi]', bulk).forEach((inp) => {
      inp.addEventListener('change', () => commit(inp));
      inp.addEventListener('focus', () => inp.select());
    });
    on('click', '[data-tc-qi-dec],[data-tc-qi-inc]', (e, b) => {
      const inp = $('[data-tc-qi]', b.parentElement);
      if (!inp) return;
      const q = Math.max(0, qtyOf(Number(inp.dataset.vid)) + (b.matches('[data-tc-qi-dec]') ? -1 : 1));
      inp.value = q > 0 ? String(q) : '';
      commit(inp);
    });
    const openPaste = $('[data-tc-open-paste]', bulk);
    const details = $('#paste', bulk);
    const area = $('[data-tc-paste]', bulk);
    if (openPaste && details)
      openPaste.addEventListener('click', (e) => {
        e.preventDefault();
        details.open = true;
        details.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        if (area) area.focus({ preventScroll: true });
      });
    if (!area) return;
    const list = $('[data-tc-paste-list]', bulk);
    const count = $('[data-tc-paste-count]', bulk);
    const add = $('[data-tc-paste-add]', bulk);
    const emptyText = add ? add.textContent : '';
    let parsed = [];
    const show = () => {
      parsed = parseLines(area.value);
      const ok = parsed.filter((p) => p.status === 'ok').length;
      const warn = parsed.filter((p) => p.status === 'warn').length;
      const bad = parsed.filter((p) => p.status === 'bad').length;
      const valid = parsed.filter((p) => p.status !== 'bad' && p.qty > 0);
      count.textContent = parsed.length ? T('paste_count', { ok, warn, bad }) : '';
      list.hidden = !parsed.length;
      list.innerHTML = parsed
        .map((p) => {
          const mark = p.status === 'ok' ? '\u2713' : p.status === 'warn' ? '!' : '\u2715';
          const what = p.status !== 'bad' && p.s ? (p.capped ? T('paste_some', { label: p.s.label }) : T('paste_line', { qty: p.qty, label: p.s.label })) + ' ' : '';
          const right = p.status !== 'bad' && p.qty && !p.capped ? (p.s.kind === 'needle' ? P('n_boxes', p.qty) : T('times', { qty: p.qty })) : '';
          return (
            `<li><span class="${p.status}" aria-label="${esc(T('paste_' + p.status))}">${mark}</span>` +
            `<span><b>${esc(T('paste_ln', { n: p.n }))}</b> ${esc(p.raw)}<br><span class="${p.status}">${esc(what + p.msg)}</span></span><span>${esc(right)}</span></li>`
          );
        })
        .join('');
      add.disabled = !valid.length;
      add.textContent = valid.length ? P('paste_add', valid.length) : emptyText;
    };
    area.addEventListener('input', show);
    const sample = $('[data-tc-paste-sample]', bulk);
    if (sample)
      sample.addEventListener('click', () => {
        area.value = pasteSample;
        show();
        area.focus();
      });
    add.addEventListener('click', () => {
      const valid = parsed.filter((p) => p.status !== 'bad' && p.qty > 0);
      if (!valid.length) return;
      addLoose(
        valid.map((p) => ({ id: p.s.id, qty: p.qty })),
        P('pasted', valid.length)
      );
      area.value = parsed
        .filter((p) => p.status === 'bad')
        .map((p) => p.raw)
        .join('\n');
      show();
    });
  }

  /** Paste parser: "0.22x30 10", "0.22 x 30 mm, 10", "10 x 0.25x40", "sharps 2". Sizes and add-on names only, no SKUs. */
  const ALIASES = { sharps: ['SHARPS', 'SHARP'], pads: ['PADS', 'PAD', 'ALCOHOL'], cupping: ['CUPPING', 'CUPS'] };
  function parseLines(text) {
    const out = [];
    const seen = new Map();
    const diams = (D.needles || []).map((n) => Number(n.d).toFixed(2));
    String(text)
      .split(/\r?\n/)
      .forEach((line, i) => {
        const raw = line.trim();
        if (!raw || raw[0] === '#') return;
        const n = i + 1;
        const up = raw.toUpperCase().replace(/[\u00d7*]/g, 'X');
        const bad = (msg) => out.push({ raw, n, status: 'bad', msg });
        if (/\bMNU|\bSKU\b|TC-/.test(up)) return bad(T('paste_no_sku'));
        let s = null;
        let rest = up;
        const dim = up.match(/(0?\.\d{2})\s*(?:MM)?\s*X\s*(\d{2,3})\s*(?:MM)?/);
        if (dim) {
          const d = parseFloat(dim[1]).toFixed(2);
          const len = String(parseInt(dim[2], 10));
          rest = up.replace(dim[0], ' ');
          const needle = (D.needles || []).find((x) => Number(x.d).toFixed(2) === d);
          if (!needle) return bad(T('paste_no_d', { d, list: diams.join(', ') }));
          const v = needle.variants.find((x) => x.len === len);
          if (!v) return bad(T('paste_no_len', { d, lens: needle.variants.map((x) => x.len).join(', '), len }));
          s = BY.get(v.id);
        } else {
          const a = (D.acc || []).find((x) => (ALIASES[x.key] || [x.key.toUpperCase()]).some((w) => new RegExp('\\b' + w + '\\b').test(up)));
          if (!a) return bad(T('paste_unread'));
          s = BY.get(a.id);
          (ALIASES[a.key] || []).forEach((w) => {
            rest = rest.replace(new RegExp('\\b' + w + '\\b', 'g'), ' ');
          });
        }
        const nums = rest.match(/-?\d+(\.\d+)?/g);
        let qty = 1;
        let msg = '';
        if (nums && nums.length) {
          const v = parseFloat(nums[nums.length - 1]);
          if (!Number.isInteger(v) || v <= 0) return bad(T('paste_bad_qty', { got: nums[nums.length - 1] }));
          qty = v;
        } else msg = T('paste_no_qty') + ' ';
        const prior = seen.get(s.id) || 0;
        if (prior) msg += T('paste_merged', { label: s.short }) + ' ';
        seen.set(s.id, prior + qty);
        if (s.stock === 'out') return bad(msg + T('sold_out_label', { label: s.label }));
        if (s.max != null) {
          const avail = capAdd(s.id) - prior;
          if (avail <= 0) return bad(msg + T('paste_none_left'));
          if (qty > avail) {
            out.push({ raw, n, status: 'warn', msg: msg + T('paste_limited'), s, qty: avail, capped: true });
            return;
          }
        }
        out.push({ raw, n, status: msg ? 'warn' : 'ok', msg: msg.trim() || T('paste_ready'), s, qty });
      });
    return out;
  }

  /* ---------- Schools: class-kit builder ---------- */

  const schools = $('[data-tc-schools]');
  const SCH = { kits: [], kit: null, n: 24, spares: true, max: 500 };
  function kitLines(k, n, spares) {
    const m = new Map();
    const put = (id, q) => m.set(id, (m.get(id) || 0) + q);
    k.per.forEach((p) => put(p.id, p.qty * n));
    (k.group || []).forEach((p) => put(p.id, p.qty * Math.ceil(n / (k.every || 1))));
    if (spares) k.per.filter((p) => p.needle).forEach((p) => put(p.id, Math.ceil(n * 0.1)));
    return Array.from(m)
      .filter(([id]) => BY.has(id))
      .map(([id, qty]) => lineOf(id, qty, BY.get(id).price));
  }
  /** PROPOSED per-student price: nearest .99 at case prices, never above list per student. */
  function kitPrice(k, n, spares) {
    const lines = kitLines(k, n, spares);
    const loose = quote({ loose: lines });
    const per = BUNDLES ? Math.min(near99(loose.proposed / n), floor99(loose.list / n)) : loose.list / n;
    const price = BUNDLES ? Math.max(0, per) * n : loose.list;
    return { lines, per, price, list: loose.list, boxes: loose.boxes, bundle: { name: T('kit_bundle', { name: k.name, n }), price, lines } };
  }
  function renderSchools() {
    if (!schools || !SCH.kit) return;
    const k = SCH.kit;
    const kp = kitPrice(k, SCH.n, SCH.spares);
    const q = quote({ bundles: [kp.bundle] });
    SCH.kp = kp;
    const short = kp.lines.filter((l) => l.qty > capAdd(l.id));
    const ul = $('[data-tc-kit-lines]', schools);
    if (ul)
      ul.innerHTML = kp.lines
        .slice()
        .sort(sortLines)
        .map((l) => {
          const s = BY.get(l.id);
          return (
            `<li${s.color ? ` style="--c:${esc(s.color)}"` : ''}><span>${s.color ? '<span class="sw" aria-hidden="true"></span>' : ''}${l.qty} &times; ${esc(s.label)}` +
            `${s.stock !== 'ok' ? ` <b class="err-t">\u00b7 ${esc(s.stock === 'out' ? T('sold_out') : T('low_stock'))}</b>` : ''}</span><span>${mh(l.price * l.qty)}</span></li>`
          );
        })
        .join('');
    const sh = $('[data-tc-kit-short]', schools);
    if (sh) {
      sh.hidden = !short.length;
      sh.textContent = short.length ? T('kit_short', { sizes: short.map((l) => BY.get(l.id).short).join(', ') }) : '';
    }
    const set = (sel, v) => {
      const el = $(sel, schools);
      if (el) el.textContent = v;
    };
    set('[data-kit-sum-list]', money(kp.list));
    set('[data-kit-sum-prop]', money(q.proposed));
    set('[data-kit-sum-note]', T('kit_note', { boxes: kp.boxes, per: money(kp.per), n: SCH.n }));
    set('[data-kit-sum-per]', money(kp.per));
    const kitSave = Math.max(0, kp.list - q.proposed);
    set('[data-kit-sum-save]', kitSave > 0 ? T('pack_save_pct', { price: money(kitSave), pct: savePct(kitSave, kp.list) }) : '');
    set('[data-kit-sum-box]', kp.boxes > 0 ? T('pack_per_box', { price: money(Math.round(q.proposed / kp.boxes)) }) : '');
    SCH.kits.forEach((kit) => {
      const opt = $(`[data-tc-kit-opt][value="${CSS.escape(kit.id)}"]`, schools);
      const lab = opt && opt.closest('label');
      if (!lab) return;
      const at = kitPrice(kit, SCH.n, SCH.spares);
      const perEl = $('[data-kit-per]', lab);
      const listEl = $('[data-kit-list]', lab);
      if (perEl) perEl.textContent = money(at.per);
      if (listEl) listEl.textContent = BUNDLES ? T('list_per', { price: money(at.list / SCH.n) }) : money(at.list / SCH.n);
    });
    const add = $('[data-tc-kit-add]', schools);
    if (add) {
      add.disabled = !kp.lines.length || short.length > 0;
      add.textContent = T('add_kit_price', { price: money(kp.list) });
    }
    $$('[data-tc-stu-set]', schools).forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.tcStuSet) === SCH.n)));
    const inp = $('[data-tc-students]', schools);
    if (inp && document.activeElement !== inp) inp.value = SCH.n;
    setBarQuiet({ label: T('add_kit'), hint: T('kit_hint', { n: SCH.n, price: money(kp.list) }), onClick: addKit, disabled: !kp.lines.length || short.length > 0 });
  }
  function addKit() {
    const kp = SCH.kp;
    if (!kp || !kp.lines.length) return;
    const name = kp.bundle.name;
    if (BUNDLES && kp.price > 0 && kp.price < kp.list) addBundle(name, kp.lines, kp.price, 'packs_bundles');
    else addLoose(kp.lines, name);
  }
  function initSchools() {
    if (!schools) return;
    try {
      SCH.kits = JSON.parse($('[data-tc-kits]', schools).textContent) || [];
    } catch (e) {
      SCH.kits = [];
    }
    SCH.n = Number(schools.dataset.students) || 24;
    SCH.max = Number(schools.dataset.max) || 500;
    const sp = $('[data-tc-spares]', schools);
    SCH.spares = sp ? sp.checked : true;
    const checked = $('[data-tc-kit-opt]:checked', schools);
    SCH.kit = SCH.kits.find((k) => checked && k.id === checked.value) || SCH.kits[0] || null;
    const msg = $('[data-tc-stu-msg]', schools);
    const range = msg ? msg.textContent : '';
    const setN = (v) => {
      const n = Math.max(1, Math.min(SCH.max, Math.round(v || 0)));
      if (msg) {
        msg.textContent = v > SCH.max ? T('stu_max', { max: SCH.max }) : v < 1 ? T('stu_min') : range;
        msg.classList.toggle('err-t', v > SCH.max || v < 1);
      }
      SCH.n = n;
      renderSchools();
    };
    $$('[data-tc-kit-opt]', schools).forEach((r) =>
      r.addEventListener('change', () => {
        SCH.kit = SCH.kits.find((k) => k.id === r.value) || SCH.kit;
        renderSchools();
      })
    );
    $$('[data-tc-stu]', schools).forEach((b) => b.addEventListener('click', () => setN(SCH.n + Number(b.dataset.tcStu))));
    $$('[data-tc-stu-set]', schools).forEach((b) => b.addEventListener('click', () => setN(Number(b.dataset.tcStuSet))));
    const inp = $('[data-tc-students]', schools);
    if (inp) inp.addEventListener('change', () => setN(parseInt(inp.value, 10)));
    if (sp)
      sp.addEventListener('change', () => {
        SCH.spares = sp.checked;
        renderSchools();
      });
    const add = $('[data-tc-kit-add]', schools);
    if (add) add.addEventListener('click', addKit);
    renderSchools();
  }

  /* ---------- Reorder ---------- */

  on('click', '[data-tc-reorder-add]', (e, b) => {
    let lines = [];
    try {
      lines = JSON.parse(b.dataset.tcReorderAdd);
    } catch (err) {}
    addLoose(lines, T('order_name', { name: b.dataset.name }));
  });
  on('click', '[data-tc-reuse-po]', (e, b) => {
    setPo(b.dataset.tcReusePo);
    notify('info', T('po_set', { po: b.dataset.tcReusePo }));
  });
  on('click', '[data-tc-hist-add]', (e, b) => {
    const h = store.get(HKEY, [])[Number(b.dataset.tcHistAdd)];
    if (h) addLoose(h.lines.filter((l) => BY.has(Number(l.id))), T('previous_tray'));
  });
  function renderHistory() {
    const ul = $('[data-tc-hist]');
    if (!ul) return;
    const h = store.get(HKEY, []);
    const list = (Array.isArray(h) ? h : []).map((x, i) => ({ x, i, lines: (x.lines || []).filter((l) => BY.has(Number(l.id))) })).filter((r) => r.lines.length);
    const empty = $('[data-tc-hist-empty]');
    if (empty) empty.hidden = list.length > 0;
    ul.hidden = !list.length;
    ul.innerHTML = list
      .map(({ x, i, lines }) => {
        const q = quote({ loose: lines.map((l) => lineOf(Number(l.id), Number(l.qty), BY.get(Number(l.id)).price)) });
        const d = new Date(x.at);
        const date = isNaN(d) ? '' : d.toLocaleDateString(document.documentElement.lang || undefined, { month: 'short', day: 'numeric', year: 'numeric' });
        const po = x.po ? ` \u00b7 ${T('po_short', { po: x.po })}` : '';
        return (
          `<li><div class="hist__h"><b>${esc(date + po)}</b><span class="mono">${esc(T('hist_sum', { boxes: q.boxes, price: money(q.list) }))}</span></div>` +
          `<span class="mono stat">${esc(lines.map((l) => `${l.qty}\u00d7 ${BY.get(Number(l.id)).short}`).join(' \u00b7 '))}</span>` +
          `<details class="hist__d"><summary class="mono">${esc(T('by_line'))}</summary><ul class="hist__l">` +
          lines
            .map((l) => {
              const s = BY.get(Number(l.id));
              return `<li><span class="mono">${esc(T('line_ordered', { label: s.label, qty: l.qty }))}</span>${stepperHTML(s.id, s.label, true)}</li>`;
            })
            .join('') +
          `</ul></details>` +
          `<div class="btns"><button type="button" class="btn btn--go" data-tc-hist-add="${i}">${esc(T('add_all'))}</button>` +
          (x.po ? `<button type="button" class="btn" data-tc-reuse-po="${esc(x.po)}">${esc(T('reuse_po', { po: x.po }))}</button>` : '') +
          `</div></li>`
        );
      })
      .join('');
  }

  /* ---------- Cart page ---------- */

  const cartPage = () => $('[data-tc-cart-page]');
  const refreshCartPage = debounce(() => {
    const cp = cartPage();
    if (!cp || !cp.dataset.section) return;
    const hadForm = !!$('#cart-form', cp);
    if (!hadForm || cart.item_count === 0) {
      if (hadForm !== cart.item_count > 0) location.reload();
      return;
    }
    fetch(`${R.cart}?section_id=${encodeURIComponent(cp.dataset.section)}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : Promise.reject(r)))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = $('[data-tc-cart-page]', doc);
        if (!fresh) return;
        ['.tray__lines', '.co-panel .sum', '[data-tc-next]'].forEach((sel) => {
          const a = $(sel, cp);
          const b = $(sel, fresh);
          if (a && b) keepFocusLine(a, () => a.replaceWith(b));
        });
        renderCartProp();
      })
      .catch(() => {});
  }, 250);
  function keepFocusLine(el, fn) {
    const a = document.activeElement;
    const li = a && el.contains(a) && a.closest('[data-key]');
    const part = li && ['[data-tc-kinc]', '[data-tc-kdec]', '[data-tc-kqty]'].find((s) => a.matches(s));
    fn();
    if (!li) return;
    const row = $(`.tline[data-key="${CSS.escape(li.dataset.key)}"]`);
    let n = row && $(part || '[data-tc-kqty]', row);
    if (n && n.disabled) n = $('[data-tc-kqty]', row);
    if (!n) n = $('.tray__lines [data-tc-kqty]', cartPage() || document);
    if (n) n.focus();
  }
  function renderCartProp() {
    const el = $('[data-tc-cart-prop]');
    if (el) el.innerHTML = propHTML(cartQuote());
  }
  on('click', '[data-tc-line-rm]', (e, a) => {
    e.preventDefault();
    removeKeys([a.dataset.tcLineRm]);
  });
  on('change', '[data-tc-note]', (e, t) => {
    queue(() => api(js(R.update), { note: t.value }).then(setCart));
  });

  /* ---------- PO and sample request forms ---------- */

  function cartSummary() {
    const q = cartQuote();
    if (!q.lines.length) return T('sum_empty');
    const lines = q.lines
      .slice()
      .sort(sortLines)
      .map((l) => `- ${l.qty} \u00d7 ${l.label || labelOf(l.id)}`);
    let tail = T('sum_list', { price: money(q.list) });
    if ((VOL || CLR || SHARPS_OFFER || BUNDLES) && q.savings > 0) tail += ' \u00b7 ' + T('sum_prop', { price: money(q.proposed) });
    return lines.join('\n') + '\n' + tail;
  }
  function kitSummary() {
    const kp = SCH.kp;
    if (!kp) return '';
    let head = T('sum_kit', { name: SCH.kit.name, n: SCH.n });
    if (SCH.spares) head += ' ' + T('sum_spares');
    const lines = kp.lines
      .slice()
      .sort(sortLines)
      .map((l) => `- ${l.qty} \u00d7 ${BY.get(l.id).label}`);
    let tail = T('sum_list', { price: money(kp.list) });
    if (BUNDLES && kp.price < kp.list) tail += ' \u00b7 ' + T('sum_kit_prop', { per: money(kp.per), price: money(kp.price) });
    return head + '\n' + lines.join('\n') + '\n' + tail;
  }
  function initForms() {
    $$('form[data-tc-form]').forEach((form) => {
      form.addEventListener('submit', (e) => {
        const ctx = form.dataset.source ? form : $('[data-source]', form);
        const source = ctx ? ctx.dataset.source : '';
        const context = ctx ? ctx.dataset.context : '';
        const summary = source === 'kit' ? kitSummary() : source === 'cart' ? cartSummary() : '';
        const poNum = $('[data-tc-po-num]', form);
        if (poNum && !poNum.value.trim() && cartPo()) poNum.value = cartPo();
        const sumInp = $('[data-tc-summary]', form);
        if (sumInp) sumInp.value = summary ? `${context}:\n${summary}` : '';
        if (!form.dataset.mailto) return;
        e.preventDefault();
        const rows = [];
        let org = '';
        Array.from(form.elements).forEach((el) => {
          const m = el.name && el.name.match(/^contact\[(.+)\]$/);
          if (!m || el.type === 'hidden' || el.type === 'submit') return;
          const k = m[1] === 'email' ? T('f_email') : m[1] === 'name' ? T('f_name') : m[1];
          const v = el.type === 'checkbox' ? (el.checked ? T('yes') : T('no')) : el.value.trim() || T('none');
          if (m[1] === 'Organization') org = el.value.trim();
          rows.push(`${k}: ${v}`);
        });
        const body = rows.join('\n') + (summary ? `\n\n${context}:\n${summary}\n` : '\n');
        const subject = (form.dataset.subject || '') + (org ? ` \u00b7 ${org}` : '');
        location.href = `mailto:${form.dataset.mailto}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      });
    });
    $$('[data-ok-for]').forEach((el) => {
      if (location.hash && location.hash !== '#' + el.dataset.okFor) el.hidden = true;
    });
  }

  /* ---------- Quote PDF (assets/tenchi-quote.js loads on first tap) ---------- */

  let quoteLib = null;
  const loadQuote = () =>
    quoteLib ||
    (quoteLib = new Promise((res, rej) => {
      if (window.TenchiQuote) return res(window.TenchiQuote);
      const s = document.createElement('script');
      s.src = R.quoteJs;
      s.async = true;
      s.onload = () => (window.TenchiQuote ? res(window.TenchiQuote) : rej(new Error('quote')));
      s.onerror = () => {
        quoteLib = null;
        rej(new Error('quote'));
      };
      document.head.append(s);
    }));

  function quoteDoc(title, q, note) {
    const now = new Date();
    const p2 = (n) => String(n).padStart(2, '0');
    const no = `TQ-${now.getFullYear()}${p2(now.getMonth() + 1)}${p2(now.getDate())}-${p2(now.getHours())}${p2(now.getMinutes())}`;
    const lang = document.documentElement.lang || 'en-US';
    const fmt = (d) => d.toLocaleDateString(lang, { year: 'numeric', month: 'long', day: 'numeric' });
    const agg = new Map();
    q.lines.forEach((l) => {
      const k = l.id + ':' + l.price;
      const a = agg.get(k) || Object.assign({}, l, { qty: 0 });
      a.qty += l.qty;
      agg.set(k, a);
    });
    const lines = Array.from(agg.values())
      .filter((l) => l.qty > 0)
      .sort(sortLines)
      .map((l) => {
        const s = BY.get(l.id);
        return { label: s && s.kind === 'needle' ? T('pdf_needle', { box: s.box, d: s.d, len: s.len }) : l.label || labelOf(l.id), qty: String(l.qty), each: money(l.price), total: money(l.price * l.qty) };
      });
    const prop = q.savings > 0 && (VOL || CLR || SHARPS_OFFER || BUNDLES);
    const anyTag = proposed('volume_ladder') || proposed('clearance') || proposed('sharps_deal') || proposed('packs_bundles');
    const rows = [{ k: P('pdf_boxes', q.boxes), v: '' }, { k: T('pdf_list_total'), v: money(q.list) }];
    if (prop) {
      q.bundles.forEach((b) => rows.push({ k: T('fixed_price', { name: b.name }), v: '-' + money(b.save), tag: proposed(b.offerKey || 'packs_bundles') }));
      if (q.save.cases > 0) rows.push({ k: T('case_price', { cases: caseText(q.cases) || T('volume_price', { pct: String(q.pct), n: q.volumeBoxes }) }), v: '-' + money(q.save.cases), tag: proposed('volume_ladder') });
      if (q.save.clearance > 0) rows.push({ k: T('clearance_price'), v: '-' + money(q.save.clearance), tag: proposed('clearance') });
      if (q.save.sharps > 0) rows.push({ k: T('sharps_price', { price: money(D.sharpsWith) }), v: '-' + money(q.save.sharps), tag: proposed('sharps_deal') });
    }
    const shipOn = (prop ? q.proposed : q.list) >= FREE;
    rows.push({ k: T('shipping'), v: FREE && shipOn ? T('pdf_ship_free', { amount: moneyShort(FREE) }) : T('pdf_ship_later', { amount: moneyShort(FREE) }) });
    rows.push({ k: T('pdf_total'), v: money(prop ? q.proposed : q.list), bold: true, tag: prop && anyTag });
    const fine = [prop ? (anyTag ? T('pdf_fine_prop') : T('pdf_fine_live')) : T('pdf_fine_list'), T('pdf_fine_valid'), T('pdf_fine_contact', { email: D.email, site: location.host })];
    const po = cartPo();
    return {
      no,
      filename: `tenchi-quote-${no}.pdf`,
      docTitle: T('pdf_doc_title', { shop: D.shop, no }),
      author: D.shop,
      logo: R.quoteLogo,
      heading: T('pdf_quote'),
      meta: [T('pdf_no', { no }), T('pdf_date', { date: fmt(now) }), T('pdf_valid', { date: fmt(new Date(now.getTime() + 30 * 864e5)) })],
      title,
      po: po ? T('pdf_po', { po }) : '',
      note: note || '',
      cols: [T('pdf_item'), T('pdf_qty'), T('pdf_each'), T('pdf_list')],
      lines,
      rows,
      tagText: T('proposed_tag'),
      fine
    };
  }
  function renderQuoteButtons() {
    const n = itemsNow();
    $$('[data-tc-quote]').forEach((b) => {
      if (b.dataset.tcBusy) return;
      b.disabled = b.dataset.tcQuote === 'kit' ? !(SCH.kp && SCH.kp.lines.length) : n <= 0;
    });
  }
  on('click', '[data-tc-quote]', async (e, b) => {
    const status = document.getElementById(b.getAttribute('aria-describedby'));
    const say = (t) => {
      if (status) status.textContent = t;
    };
    b.dataset.tcBusy = '1';
    b.disabled = true;
    say(T('quote_making'));
    try {
      let doc;
      if (b.dataset.tcQuote === 'kit') {
        const kp = SCH.kp;
        const parts = [];
        if (BUNDLES && kp.price < kp.list) parts.push(T('quote_kit_note', { per: money(kp.per) }) + (proposed('packs_bundles') ? ` (${T('proposed_tag')})` : ''));
        if (SCH.spares) parts.push(T('quote_spares'));
        const note = parts.join(' \u00b7 ');
        doc = quoteDoc(T('quote_kit_title', { title: b.dataset.title, name: SCH.kit.name, n: SCH.n }), quote({ bundles: [kp.bundle] }), note);
      } else {
        await flushNow();
        const q = cartQuote();
        let title = b.dataset.title || T('quote_title');
        if (!b.hasAttribute('data-tc-quote-tray')) title = T('quote_cart_title', { title, boxes: P('n_boxes', q.boxes) });
        if (bulk && VOL && q.boxes >= (CASES[0] ? CASES[0].size : Infinity)) title += ' ' + T('quote_case_sel', { size: target });
        doc = quoteDoc(title, q, '');
      }
      const lib = await loadQuote();
      await lib.download(doc);
      say(T('quote_done', { no: doc.no }));
    } catch (err) {
      say(T('quote_fail'));
      notify('error', T('quote_fail'));
    }
    delete b.dataset.tcBusy;
    renderQuoteButtons();
  });

  /* ---------- Paid invoice PDF (every order; tax lines + Paid status) ---------- */

  function invoiceDoc(inv) {
    const lines = (inv.lines || []).map((l) => ({
      label: l.label,
      qty: String(l.qty),
      each: l.each,
      total: l.total
    }));
    const rows = [];
    rows.push({ k: T('pdf_inv_subtotal'), v: inv.subtotal });
    (inv.discounts || []).forEach((d) => rows.push({ k: d.title, v: d.amount }));
    (inv.shipping || []).forEach((s) => rows.push({ k: T('pdf_inv_shipping', { title: s.title }), v: s.price }));
    (inv.taxes || []).forEach((t) => rows.push({ k: T('pdf_inv_tax', { title: t.title, rate: t.rate }), v: t.price }));
    rows.push({ k: T('pdf_inv_total'), v: inv.total, bold: true });
    rows.push({ k: T('pdf_inv_status'), v: inv.status });
    if (inv.method) rows.push({ k: T('pdf_inv_method'), v: inv.method });
    const bill = (inv.bill || []).filter(Boolean).join('\n');
    const ship = (inv.ship || []).filter(Boolean).join('\n');
    const noteParts = [];
    if (bill) noteParts.push(T('pdf_inv_bill') + '\n' + bill);
    if (ship) noteParts.push(T('pdf_inv_ship') + '\n' + ship);
    const fine = [T('pdf_inv_deduct'), T('pdf_inv_ein'), T('pdf_fine_contact', { email: D.email, site: location.host })];
    return {
      no: inv.no,
      filename: `tenchi-invoice-${String(inv.no).replace(/[^\w-]+/g, '')}.pdf`,
      docTitle: T('pdf_inv_doc', { shop: D.shop, no: inv.no }),
      author: D.shop,
      logo: R.quoteLogo,
      heading: T('pdf_invoice'),
      meta: [T('pdf_inv_no', { no: inv.no }), T('pdf_date', { date: inv.date }), T('pdf_inv_paid_line', { status: inv.status })],
      title: T('pdf_inv_title', { shop: D.shop }),
      po: inv.po ? T('pdf_po', { po: inv.po }) : '',
      note: noteParts.join('\n\n'),
      cols: [T('pdf_item'), T('pdf_qty'), T('pdf_inv_each'), T('pdf_inv_amount')],
      lines,
      rows,
      tagText: ' ',
      fine
    };
  }
  async function downloadInvoice(inv, statusEl) {
    const say = (t) => {
      if (statusEl) statusEl.textContent = t;
    };
    say(T('invoice_making'));
    const doc = invoiceDoc(inv);
    const lib = await loadQuote();
    await lib.download(doc);
    say(T('invoice_done', { no: doc.no }));
    return doc.no;
  }
  function initInvoice() {
    const host = $('[data-tc-invoice]');
    if (!host) return;
    let inv;
    try {
      inv = JSON.parse(host.textContent);
    } catch (e) {
      return;
    }
    const btn = $('[data-tc-invoice-dl]');
    const status = document.getElementById(btn && btn.getAttribute('aria-describedby'));
    const run = async () => {
      if (!btn) return;
      btn.dataset.tcBusy = '1';
      btn.disabled = true;
      try {
        await downloadInvoice(inv, status);
      } catch (err) {
        if (status) status.textContent = T('invoice_fail');
        notify('error', T('invoice_fail'));
      }
      delete btn.dataset.tcBusy;
      btn.disabled = false;
    };
    if (btn) btn.addEventListener('click', run);
    try {
      if (/(?:^|[?&])invoice=1(?:&|$)/.test(location.search)) run();
    } catch (e) {}
  }

  /* ---------- Media: hero loop and loop bands ---------- */

  function loopVideo(host, pick, cls) {
    if (!host || !('IntersectionObserver' in window)) return;
    if (navigator.connection && navigator.connection.saveData) return;
    const rm = matchMedia('(prefers-reduced-motion: reduce)');
    let v = null;
    let armed = false;
    const io = new IntersectionObserver((es) =>
      es.forEach((en) => {
        if (en.isIntersecting) {
          const p = en.target.play();
          if (p && p.catch) p.catch(() => {});
        } else en.target.pause();
      })
    );
    const kill = () => {
      if (!v) return;
      io.unobserve(v);
      v.pause();
      v.remove();
      v = null;
    };
    const live = (u) =>
      fetch(u, { method: 'HEAD' })
        .then((r) => r.ok)
        .catch(() => false);
    const make = () => {
      if (!armed || rm.matches || v) return;
      const s = pick();
      if (!s || !(s.webm || s.mp4)) return;
      Promise.all([s.webm ? live(s.webm) : false, s.mp4 ? live(s.mp4) : false]).then(([webmOk, mp4Ok]) => {
        if (!armed || rm.matches || v || !(webmOk || mp4Ok)) return;
        v = document.createElement('video');
        v.className = cls;
        v.muted = true;
        v.defaultMuted = true;
        v.loop = true;
        v.autoplay = true;
        v.playsInline = true;
        v.setAttribute('muted', '');
        v.setAttribute('playsinline', '');
        v.setAttribute('aria-hidden', 'true');
        v.setAttribute('disablepictureinpicture', '');
        v.tabIndex = -1;
        v.preload = 'auto';
        if (s.poster) v.poster = s.poster;
        [['mp4', mp4Ok ? s.mp4 : ''], ['webm', webmOk ? s.webm : '']].forEach(([t, u]) => {
          if (!u) return;
          const so = document.createElement('source');
          so.src = u;
          so.type = 'video/' + t;
          v.append(so);
        });
        const el = v;
        el.addEventListener('playing', () => el.classList.add('is-on'));
        el.addEventListener('error', () => kill());
        host.append(el);
        io.observe(el);
      });
    };
    const arm = () =>
      setTimeout(() => {
        const run = () => {
          armed = true;
          make();
        };
        if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 1500 });
        else run();
      }, 150);
    if (document.readyState === 'complete') arm();
    else window.addEventListener('load', arm, { once: true });
    rm.addEventListener('change', () => (rm.matches ? kill() : make()));
    return { reset: () => (kill(), make()) };
  }
  /* ---------- Motion: draw, rise and shine on scroll; a box flies to the tray count on add ---------- */

  const RM = matchMedia('(prefers-reduced-motion: reduce)');
  function initProof() {
    const root = $('[data-tc-proof]');
    if (!root) return;
    const nums = $$('[data-tc-count]', root);
    if (!nums.length || RM.matches || !('IntersectionObserver' in window)) return;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const run = (el) => {
      if (el.dataset.tcDone) return;
      el.dataset.tcDone = '1';
      const to = Number(el.dataset.tcCount);
      const dec = Number(el.dataset.tcDec || 0);
      const suf = el.dataset.tcSuffix || '';
      if (!isFinite(to)) return;
      const fmt = (n) => (dec ? n.toFixed(dec) : String(Math.round(n))) + suf;
      const t0 = performance.now();
      const dur = 900;
      const tick = (now) => {
        const p = Math.min(1, (now - t0) / dur);
        el.textContent = fmt(to * ease(p));
        if (p < 1) requestAnimationFrame(tick);
        else el.textContent = fmt(to);
      };
      el.textContent = fmt(0);
      requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          $$('[data-tc-count]', en.target).forEach(run);
          io.unobserve(en.target);
        });
      },
      { threshold: 0.35 }
    );
    io.observe(root);
  }
  function initMotion() {
    const els = $$('.tc-draw, .tc-rise, .tc-shine');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        let k = 0;
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          const el = en.target;
          if (el.classList.contains('tc-draw')) el.style.setProperty('--dl', Math.min(k++, 8) * 70 + 'ms');
          el.classList.add('is-in');
          io.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -6% 0px' }
    );
    els.forEach((el) => io.observe(el));
  }

  const TAP_SEL =
    '[data-tc-add],[data-tc-inc],[data-tc-dec],[data-tc-plus],[data-tc-minus],[data-tc-kinc],[data-tc-kdec],[data-tc-qi-inc],[data-tc-qi-dec],' +
    '[data-tc-pack-add],[data-tc-quick],[data-tc-bar-cta],[data-tc-reorder-add],[data-tc-hist-add],[data-tc-kit-add],[data-tc-paste-add],' +
    '[data-tc-qty],[data-tc-qi],[data-tc-kqty]';
  const noteTap = (e) => {
    const el = e.target.closest && e.target.closest(TAP_SEL);
    if (!el) return;
    tap = { at: Date.now(), r: el.getBoundingClientRect(), c: getComputedStyle(el).getPropertyValue('--c').trim() };
  };
  document.addEventListener('click', noteTap, true);
  document.addEventListener('change', noteTap, true);

  function bump(dir) {
    const el = $('.tray__count');
    if (!el || !el.animate) return;
    el.animate(
      dir > 0
        ? [{ transform: 'scale(1)' }, { transform: 'scale(1.22)', offset: 0.35 }, { transform: 'scale(.96)', offset: 0.7 }, { transform: 'scale(1)' }]
        : [{ transform: 'scale(1)' }, { transform: 'scale(.88)', offset: 0.4 }, { transform: 'scale(1)' }],
      { duration: dir > 0 ? 420 : 360, easing: 'ease-out' }
    );
  }
  /** Add: a small box arcs from the tapped control to the tray count, which bounces. Minus: it drifts back out and fades. */
  function fly(dir, from) {
    const to = $('.tray__count');
    if (RM.matches || !to || !document.body.animate) return;
    const b = to.getBoundingClientRect();
    if (!b.width) return;
    const a = from.r;
    if (!a.width || a.bottom < 0 || a.top > innerHeight) return bump(dir);
    const p0 = { x: a.left + a.width / 2, y: a.top + a.height / 2 };
    const p1 = { x: b.left + b.width / 2, y: b.top + b.height / 2 };
    const [s, e] = dir > 0 ? [p0, p1] : [p1, p0];
    const box = document.createElement('i');
    box.className = 'tc-fly';
    box.setAttribute('aria-hidden', 'true');
    if (from.c) box.style.setProperty('--c', from.c);
    document.body.append(box);
    const mx = (s.x + e.x) / 2;
    const my = Math.min(s.y, e.y) - (dir > 0 ? 90 : 40);
    const anim = box.animate(
      [
        { transform: `translate(${s.x}px,${s.y}px) scale(${dir > 0 ? 1 : 0.6})`, opacity: dir > 0 ? 1 : 0.8 },
        { transform: `translate(${mx}px,${my}px) scale(.85)`, opacity: dir > 0 ? 1 : 0.6, offset: 0.45 },
        { transform: `translate(${e.x}px,${e.y}px) scale(${dir > 0 ? 0.5 : 0.9})`, opacity: dir > 0 ? 0.9 : 0 }
      ],
      { duration: dir > 0 ? 620 : 520, easing: 'cubic-bezier(.45,0,.25,1)', fill: 'forwards' }
    );
    if (dir < 0) bump(-1);
    anim.onfinish = anim.oncancel = () => {
      box.remove();
      if (dir > 0) bump(1);
    };
  }
  function motionOnCount(items) {
    const prev = lastItems;
    lastItems = items;
    if (prev == null || items === prev || !tap || Date.now() - tap.at > 2500) return;
    const t = tap;
    tap = null;
    fly(items > prev ? 1 : -1, t);
  }

  function initMedia() {
    const hero = $('[data-tc-hero]');
    if (hero && (hero.dataset.dMp4 || hero.dataset.dWebm)) {
      const mob = matchMedia('(max-width:1023px)');
      const ctl = loopVideo(
        hero,
        () => (mob.matches ? { webm: hero.dataset.mWebm, mp4: hero.dataset.mMp4, poster: hero.dataset.mPoster } : { webm: hero.dataset.dWebm, mp4: hero.dataset.dMp4, poster: hero.dataset.dPoster }),
        'hero-media__v'
      );
      if (ctl) mob.addEventListener('change', ctl.reset);
    }
    $$('[data-tc-loop]').forEach((band) => loopVideo(band, () => ({ webm: band.dataset.webm, mp4: band.dataset.mp4, poster: band.dataset.poster }), 'media-band__v'));
  }

  /* ---------- Header, accounts, small helpers ---------- */

  function initMenu() {
    const btn = $('[data-tc-menu]');
    const nav = $('[data-tc-mnav]');
    if (!btn || !nav) return;
    const set = (open, focus) => {
      nav.hidden = !open;
      btn.setAttribute('aria-expanded', String(open));
      if (!open && focus) btn.focus();
    };
    btn.addEventListener('click', () => set(nav.hidden));
    nav.addEventListener('click', (e) => {
      if (e.target.closest('a')) set(false);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !nav.hidden) set(false, true);
    });
    matchMedia('(min-width:1024px)').addEventListener('change', (e) => {
      if (e.matches) set(false);
    });
  }
  on('submit', 'form[data-tc-confirm]', (e, f) => {
    if (!window.confirm(f.dataset.tcConfirm)) e.preventDefault();
  });
  on('click', '[data-tc-recover]', () => {
    setTimeout(() => {
      const r = document.getElementById('r-email');
      if (r) r.focus();
    }, 0);
  });
  function initAddresses() {
    if (!(window.Shopify && window.Shopify.CountryProvinceSelector)) return;
    $$('[data-tc-country]').forEach((sel) => {
      const suf = sel.dataset.tcCountry;
      if (document.getElementById('pv-' + suf)) new window.Shopify.CountryProvinceSelector('ct-' + suf, 'pv-' + suf, { hideElement: 'pv-wrap-' + suf });
    });
  }

  /* ---------- Render everything that mirrors the cart ---------- */

  function render() {
    barQuiet = true;
    renderBulk();
    renderSchools();
    renderPdpBar();
    barQuiet = false;
    renderSteppers();
    renderGauges();
    renderMatrix();
    renderNext();
    renderBar();
    renderSheet(false);
    renderCartShip();
    renderKeySteppers();
    renderPo();
    renderRx();
    renderQuoteButtons();
    renderCartProp();
    syncShipUnlock();
  }

  function hideOneTimeSubscriptionCopy() {
    $$('[data-tc-pdp]:not([data-has-plans])').forEach((pdp) => {
      $$('.shopify-subscription-policy, .shopify-payment-terms, [data-shopify="payment-terms"]', pdp).forEach((el) => el.remove());
      $$('p, span, small, div', pdp).forEach((el) => {
        const t = (el.textContent || '').replace(/\s+/g, ' ').trim();
        if (/^This item is a deferred, subscription, or recurring purchase/i.test(t) && el.childElementCount === 0) el.remove();
      });
    });
  }
  hideOneTimeSubscriptionCopy();
  const buyDyn = $('[data-tc-pdp]:not([data-has-plans]) .buy__dyn');
  if (buyDyn && window.MutationObserver) {
    new MutationObserver(hideOneTimeSubscriptionCopy).observe(buyDyn, { childList: true, subtree: true });
  }

  $$('[role="radiogroup"]').forEach(rove);
  $$('[data-tc-shop]').forEach(initShop);
  $$('[data-tc-pdp]').forEach(initPdp);
  $$('[data-tc-pack]').forEach(renderPack);
  initBulk();
  initSchools();
  initForms();
  initInvoice();
  initMenu();
  initMedia();
  initMotion();
  initProof();
  renderHistory();
  render();
  if (document.readyState === 'complete') initAddresses();
  else window.addEventListener('DOMContentLoaded', initAddresses);
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) refresh();
  });

  window.Tenchi = { quote, casePlan, nextCase, kitPrice, volumePct, volumeCost, advertisedAt, perBox, parseLines, refresh, openTray, closeTray, get cart() { return cart; } };
})();
